import { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";

import { DeckGL, GeoJsonLayer, IconLayer, MapView as MapViewComponent } from "deck.gl";
import { WebMercatorViewport } from "@deck.gl/core";
import { TripsLayer } from "@deck.gl/geo-layers";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import { Map as MapLibreMap } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import "./MapView.css";
import visorLogo from "./visor-logo.png";
import markerAccident from "./icons/markers/accident.png";
import markerHazard from "./icons/markers/hazard.png";
import markerJam from "./icons/markers/jam.png";
import markerRoadClosed from "./icons/markers/road_closed.png";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale,
  BarElement, ArcElement,
  Tooltip as ChartTooltip, Legend as ChartLegend,
} from "chart.js";

import DataLoader from "../../Components/DataLoader";
import CustomTooltip from "../../Components/CustomTooltip";
import SidebarDetail from "../../Components/SidebarDetail";
import DateField from "../../Components/DateField";
import HourField from "../../Components/HourField";
import SearchSelect from "../../Components/SearchSelect";
import WelcomeModal from "../../Components/WelcomeModal";
import {
  setSelectedRegion,
  setDateRange,
  setTimeRange,
  setSelectedCommune,
  setShowJams,
  setShowAlerts,
  setSelectedFeature,
  setTimelinePlaying,
  setCompareEnabled,
  setCompareDateRange,
  setCompareTimeRange,
  setCompareMode,
} from "../../redux/reducers/dataSlice";
import { readURL, writeURL, parseInitialViewport } from "../../urlState";


ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, ChartTooltip, ChartLegend);


const GEOLOCATION_ZOOM = 14;
const DEFAULT_ZOOM = 12;
// Vista inicial: todo Chile (el país es largo y angosto, así que zoom bajo
// centrado al sur del centro geográfico para encuadrarlo de norte a sur).
const CHILE_VIEW = { longitude: -71.0, latitude: -38.0, zoom: 3.7, pitch: 0, bearing: 0 };
// Bajo este zoom se muestra la región completa (sin filtrar por comuna). A
// partir de él, el viewport auto-selecciona la comuna para bajar detalle.
const COMMUNE_MIN_ZOOM = 13;
// A zoom ≤ este (vista país/continente) no se renderizan datos: región-wide es
// pesado e ilegible tan lejos. Sólo el basemap.
const DATA_MIN_ZOOM = 7;


const colorPalette = {
  5: [215, 19, 7, 230],
  4: [255, 123, 2, 230],
  3: [255, 173, 3, 230],
  2: [255, 204, 1, 230],
  1: [40, 180, 70, 230],
  0: [150, 150, 150, 200],
};
const getColorByLevel = (level) => colorPalette[level] || colorPalette[0];
const getGlowColor = (level) => {
  const c = getColorByLevel(level);
  return [c[0], c[1], c[2], 70];
};


// Markers de alertas para el mapa: (1) dedup por uuid — la misma alerta se
// repite en cada snapshot de 2 min, así que sin dedup se apilan decenas de
// íconos en el mismo punto; (2) declutter por zoom — 1 marker por celda de
// ~grilla en píxeles, priorizando accidentes. A más zoom, las celdas (en
// grados) se achican → se revelan más markers. Reutilizable para A y B.
function declutterAlerts(visAlerts, zoom, lat) {
  const seen = new Set();
  const uniq = [];
  for (const f of visAlerts) {
    const p = f.properties || {};
    const key = p.uuid
      || `${p.type}|${p.subtype}|${(f.geometry?.coordinates || []).join(",")}`;
    if (seen.has(key)) continue;
    seen.add(key);
    uniq.push(f);
  }
  const metersPerPixel = 156543.03392 * Math.cos((lat * Math.PI) / 180) / Math.pow(2, zoom);
  const degPerPixel = metersPerPixel / 111320;
  const CELL_PX = 44;  // separación mínima deseada entre íconos, en pantalla
  const cell = CELL_PX * degPerPixel;
  if (!Number.isFinite(cell) || cell <= 0) return uniq;
  const priority = (f) => {
    const t = f.properties?.type, s = f.properties?.subtype;
    if (s === "ACCIDENT_MAJOR") return 4;
    if (t === "ACCIDENT" || s === "ACCIDENT_MINOR") return 3;
    if (t === "HAZARD" || t === "WEATHERHAZARD") return 2;
    return 1;
  };
  const best = new Map();
  for (const f of uniq) {
    const c = f.geometry?.coordinates;
    if (!c) continue;
    const k = `${Math.round(c[0] / cell)}|${Math.round(c[1] / cell)}`;
    const prev = best.get(k);
    if (!prev || priority(f) > priority(prev)) best.set(k, f);
  }
  return Array.from(best.values());
}

// Duración total del loop de TripsLayer en ms. Cada calle se normaliza
// para completar exactamente un ciclo en este tiempo — así toda la red
// "respira" sincronizada en vez de tener trips rápidos y lentos.
const TRIPS_LOOP_MS = 6000;

const COMPARE_MODES = [
  { id: "overlay", icon: "layers",         label: "Overlay", help: "Ambas fechas superpuestas en el mismo mapa." },
  { id: "swipe",   icon: "compare",        label: "Cortina", help: "Un mapa con divisor arrastrable: A a la izquierda, B a la derecha." },
  { id: "split",   icon: "vertical_split", label: "Split",   help: "Dos mapas lado a lado moviéndose en sincronía." },
  { id: "diff",    icon: "difference",     label: "Diff",    help: "Coloreado por delta de nivel promedio por calle entre A y B." },
  { id: "blink",   icon: "flash_on",       label: "Blink",   help: "Alterna A y B cada segundo para detectar diferencias sutiles." },
];


const svgIcon = (emoji, bg = "#ff7b02") =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="16" fill="${bg}" stroke="#fff" stroke-width="2"/>
      <text x="20" y="26" text-anchor="middle" font-size="18">${emoji}</text>
    </svg>`,
  )}`;
// Iconos de diseño (line-art amarillo sobre fondo oscuro redondeado, 128px).
// Los 4 principales usan los assets de diseño; POLICE/WEATHERHAZARD/DEFAULT
// mantienen el pin emoji por no tener símbolo dedicado.
const ALERT_ICONS = {
  ACCIDENT:      { url: markerAccident,   width: 128, height: 128 },
  HAZARD:        { url: markerHazard,     width: 128, height: 128 },
  ROAD_CLOSED:   { url: markerRoadClosed, width: 128, height: 128 },
  JAM:           { url: markerJam,        width: 128, height: 128 },
  POLICE:        { url: svgIcon("👮", "#3b86ff"), width: 40, height: 40 },
  WEATHERHAZARD: { url: svgIcon("🌧️", "#5588ff"), width: 40, height: 40 },
  DEFAULT:       { url: svgIcon("•", "#888"),    width: 40, height: 40 },
};
const getAlertIcon = (f) => ALERT_ICONS[f.properties?.type] || ALERT_ICONS.DEFAULT;

// Hoy en zona horaria de Chile (donde corre el cron y se particionan los
// datos). No usar toISOString() — devuelve UTC y entre ~20:00 y ~04:00
// Chile adelanta el día.
const todayInChile = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Santiago",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};

// Hora "HH:MM" actual en Chile.
const nowHHMMInChile = () => new Intl.DateTimeFormat("en-GB", {
  timeZone: "America/Santiago", hour: "2-digit", minute: "2-digit", hour12: false,
}).format(new Date());

// Suma días a una fecha YYYY-MM-DD (positivo o negativo).
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "24 may" (es-CL, abreviado).
const fmtDayMonth = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  return new Intl.DateTimeFormat("es-CL", { day: "numeric", month: "short" }).format(d).replace(/\./g, "");
};

// Lunes de la semana del ISO date (Chile lo asume lunes-primero).
const mondayOf = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  const dow = (d.getDay() + 6) % 7; // 0=lun, 6=dom
  d.setDate(d.getDate() - dow);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Definición declarativa de presets. range() devuelve los 4 campos del
// state temporal; matches(state) detecta si el state actual coincide.
const buildPresets = () => {
  const today = todayInChile();
  const yest  = addDays(today, -1);
  const now   = nowHHMMInChile();
  return [
    { id: "today",     label: "Hoy",
      range: () => ({ dateStart: today, dateEnd: today, timeStart: null, timeEnd: null }) },
    { id: "yesterday", label: "Ayer",
      range: () => ({ dateStart: yest,  dateEnd: yest,  timeStart: null, timeEnd: null }) },
    // Última hora = desde :00 de la hora en curso hasta el momento actual.
    { id: "lasthour",  label: "Última hora",
      range: () => ({ dateStart: today, dateEnd: today, timeStart: `${now.slice(0, 2)}:00`, timeEnd: now }) },
    { id: "last24h",   label: "Últimas 24h",
      range: () => ({ dateStart: yest, dateEnd: today, timeStart: null, timeEnd: null }) },
    { id: "thisweek",  label: "Esta semana",
      range: () => ({ dateStart: mondayOf(today), dateEnd: today, timeStart: null, timeEnd: null }) },
    { id: "last7d",    label: "Últimos 7 días",
      range: () => ({ dateStart: addDays(today, -6), dateEnd: today, timeStart: null, timeEnd: null }) },
  ];
};

export default function MapView() {
  const deckRef = useRef();
  const shellRef = useRef(null);
  const dispatch = useDispatch();

  const regions        = useSelector((s) => s.dataState.regions);
  const selectedRegion = useSelector((s) => s.dataState.selectedRegion);
  const dateStart      = useSelector((s) => s.dataState.dateStart);
  const dateEnd        = useSelector((s) => s.dataState.dateEnd);
  const timeStart      = useSelector((s) => s.dataState.timeStart);
  const timeEnd        = useSelector((s) => s.dataState.timeEnd);
  // Shims: muchos consumers (labels, KPIs, layers) usan los nombres
  // viejos. Hasta migrarlos por completo, derivamos del rango. Single-day
  // → selectedDate = dateStart; single-hour → selectedHour = HH del timeStart.
  const selectedDate   = dateStart;
  const isSingleDay    = dateStart === dateEnd;
  const selectedHour   = timeStart ? timeStart.slice(0, 2) : null;
  const catalog        = useSelector((s) => s.dataState.catalog);
  const geojson        = useSelector((s) => s.dataState.geojson);
  const alertsGeojson  = useSelector((s) => s.dataState.alertsGeojson);
  const showJams       = useSelector((s) => s.dataState.showJams);
  const showAlerts     = useSelector((s) => s.dataState.showAlerts);
  const selectedCommune = useSelector((s) => s.dataState.selectedCommune);
  const communesIndex = useSelector((s) => s.dataState.communesIndex);
  const selectedFeature = useSelector((s) => s.dataState.selectedFeature);
  const timelinePlaying = useSelector((s) => s.dataState.timelinePlaying);
  const compareEnabled = useSelector((s) => s.dataState.compareEnabled);
  const compareDateStart = useSelector((s) => s.dataState.compareDateStart);
  const compareDateEnd   = useSelector((s) => s.dataState.compareDateEnd);
  const compareTimeStart = useSelector((s) => s.dataState.compareTimeStart);
  const compareTimeEnd   = useSelector((s) => s.dataState.compareTimeEnd);
  const compareDate = compareDateStart;  // shim: legacy label users
  // Sufijo de hora para las etiquetas de lado (A/B): rango horario si está
  // activo, o "día completo" si no hay filtro de hora.
  const fmtSideTime = (tStart, tEnd) => (tStart && tEnd ? `${tStart}–${tEnd}` : "día completo");
  // Fecha para las etiquetas de lado: rango "inicio → fin" si difieren, o un
  // solo día si es single-day.
  const fmtSideDate = (start, end) =>
    (end && end !== start ? `${fmtDayMonth(start)} → ${fmtDayMonth(end)}` : fmtDayMonth(start));
  const compareGeojson = useSelector((s) => s.dataState.compareGeojson);
  const compareAlertsGeojson = useSelector((s) => s.dataState.compareAlertsGeojson);
  const compareCatalog = useSelector((s) => s.dataState.compareCatalog);
  const compareMode    = useSelector((s) => s.dataState.compareMode);
  const catalogLoading = useSelector((s) => s.dataState.catalogLoading);
  const geojsonLoading = useSelector((s) => s.dataState.geojsonLoading);
  const error          = useSelector((s) => s.dataState.error);
  const isLoading      = catalogLoading || geojsonLoading;

  // ── Viewport ──────────────────────────────────────────────────
  const urlInitial = useRef(readURL()).current;
  const urlViewport = useRef(parseInitialViewport(urlInitial)).current;

  const defaultViewState = {
    longitude: urlViewport?.longitude ?? CHILE_VIEW.longitude,
    latitude:  urlViewport?.latitude  ?? CHILE_VIEW.latitude,
    zoom:      urlViewport?.zoom      ?? CHILE_VIEW.zoom,
    pitch: 0, bearing: 0,
  };
  const [viewState, setViewState] = useState(defaultViewState);
  const [stableViewState, setStableViewState] = useState(defaultViewState);
  const debounceTimer = useRef();
  const [hoverInfo, setHoverInfo] = useState(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [collapsed, setCollapsed] = useState(isMobile);
  // Modal de bienvenida al cargar (salvo deep-link con viewport explícito en URL).
  const [showWelcome, setShowWelcome] = useState(!urlViewport);
  const [selectedStreet, setSelectedStreet] = useState(null);

  // Modo auto-ocultar (toggleable + persistido): cuando está activo, el panel
  // se colapsa al hacer clic fuera y reaparece al pasar el cursor por la
  // iconbar. Default on; el usuario puede apagarlo con el checkbox del header.
  const [autoCollapse, setAutoCollapse] = useState(() => {
    try {
      const v = localStorage.getItem("clbb-waze:autoCollapse");
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("clbb-waze:autoCollapse", autoCollapse ? "1" : "0"); }
    catch { /* localStorage no disponible */ }
  }, [autoCollapse]);

  // Click fuera del panel → colapsa (sólo si auto-ocultar está activo y el
  // panel abierto). El listener se monta DESPUÉS de que abre, así que no
  // captura el mismo click que lo abrió. Excluye el shell y los poppers
  // (datepicker / dropdown) por si se renderizan en portal.
  useEffect(() => {
    if (collapsed || !autoCollapse) return;
    const onPointerDown = (e) => {
      const t = e.target;
      if (shellRef.current && shellRef.current.contains(t)) return;
      if (t.closest && t.closest(".react-datepicker, .ss-pop")) return;
      setCollapsed(true);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [collapsed, autoCollapse]);
  // Swipe (cortina): porcentaje 0..1 del ancho desde la izquierda donde
  // termina la capa A y empieza la B.
  const [swipePos, setSwipePos] = useState(0.5);
  const swipeRef = useRef(null);
  const swipeDragging = useRef(false);
  // Blink: alterna cuál lado se muestra (A o B) en modo blink.
  const [blinkSide, setBlinkSide] = useState("A");
  // Modo cinemático (animaciones): por default ON en desktop, OFF en mobile
  // por razones de batería/perf. URL/localStorage si querés persistir.
  const [cinematic, setCinematic] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= 768;
  });
  // Modo 3D: tilt + elevación de los jams por level. Bumps pitch a 50°.
  const [is3D, setIs3D] = useState(false);
  // Expansión del bloque "Personalizado" (date+hour pickers detallados).
  const [customExpanded, setCustomExpanded] = useState(false);
  // Lista de presets construida una vez por mount.
  const presetsRef = useRef(null);
  if (!presetsRef.current) presetsRef.current = buildPresets();
  const presets = presetsRef.current;
  // ¿Cuál preset coincide con el state actual? null si es custom.
  const activePresetId = (() => {
    for (const p of presets) {
      const r = p.range();
      if (r.dateStart === dateStart && r.dateEnd === dateEnd &&
          (r.timeStart || null) === (timeStart || null) &&
          (r.timeEnd   || null) === (timeEnd   || null)) {
        return p.id;
      }
    }
    return null;
  })();
  // Tick global para animaciones (segundos transcurridos desde mount).
  // Actualizado con requestAnimationFrame; throttled a ~30Hz cuando
  // cinematic está activo, parado cuando está OFF.
  const [tick, setTick] = useState(0);
  // Refs para cross-fade entre horas: guardamos los features de la hora
  // anterior y el timestamp del último swap (en mismo clock que `tick`).
  const prevHourGeojson = useRef(null);
  const lastHourSwapAt = useRef(0);
  const lastGoodGeojson = useRef(null);
  // Espejo del tick en ref así puedo leer el valor más reciente desde
  // useEffects que no quiero que dependan de tick.
  const tickRef = useRef(0);

  // Arranca en null (no "cebado"): así el auto-zoom inicial puede centrar en la
  // región aunque la URL ya traiga selectedRegion. Si la URL trae un viewport
  // explícito que cae DENTRO de la región, ese efecto lo respeta (deep-link).
  // Lo inicializamos con la región actual para que el auto-zoom NO dispare al
  // cargar: el mapa parte mostrando todo Chile (CHILE_VIEW), no una región.
  const lastZoomedRegion = useRef(selectedRegion);
  const lastZoomedCommune = useRef(null);
  // ¿El usuario eligió explícitamente fecha/hora/preset? Si NO, el default es
  // siempre "última hora con datos" y se re-aplica al cargar cada catálogo
  // (p.ej. al cambiar de comuna). Si ya viene un timeStart en la URL, lo
  // respetamos (deep-link).
  const userPickedTime = useRef(timeStart !== null);

  // ¿El último cambio de viewport vino de un PANEO del usuario? deck.gl sólo
  // dispara onViewStateChange en interacción real, así que ahí lo marcamos true.
  // Los movimientos programáticos (flyTo) lo dejan en false. Los auto-switch de
  // región/comuna por viewport sólo corren si fue paneo del usuario — así la
  // navegación explícita (combos / botón Ir / geo) no "pelea" con ellos.
  const userInteracted = useRef(false);
  const flyTo = (next) => {
    userInteracted.current = false;
    setViewState(next);
    setStableViewState(next);
  };

  // Default "última hora": mientras el usuario no elija fecha/hora/preset, al
  // cargar el catálogo (incl. al cambiar de comuna) seleccionamos la última
  // hora CON DATOS del día. Sólo aplica en single-day.
  useEffect(() => {
    if (userPickedTime.current) return;        // el usuario eligió → respetar
    if (!catalog?.hours || !isSingleDay) return;
    const today = todayInChile();
    const hourNow = parseInt(new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Santiago", hour: "2-digit", hour12: false,
    }).format(new Date()), 10);
    const startH = dateStart === today ? hourNow : 23;
    for (let h = startH; h >= 0; h--) {
      const hh = String(h).padStart(2, "0");
      if (catalog.hours[hh]?.feature_count > 0) {
        // Si es la hora en curso de hoy, la ventana llega hasta el momento
        // actual; si es una hora pasada, abarca la hora completa (hh:59).
        const end = (dateStart === today && h === hourNow) ? nowHHMMInChile() : `${hh}:59`;
        dispatch(setTimeRange({ start: `${hh}:00`, end }));
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, isSingleDay, dateStart, dispatch]);

  // Geolocalización EXPLÍCITA (botón del modal de bienvenida / sin auto-disparo).
  // Centra en la posición real del usuario y, si cae en una región conocida, la
  // selecciona. El mapa NO geolocaliza solo al cargar: parte mostrando Chile.
  const geolocateMe = () => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { longitude, latitude } = pos.coords;
        // Buscamos la COMUNA que contiene el punto sobre TODO el índice (no por
        // región): los bboxes de región son rectángulos que se solapan entre
        // regiones vecinas (p.ej. Valparaíso y RM), así que matchear por región
        // primero elegía mal. La comuna (bbox chico + centroide más cercano)
        // desambigua, y de ahí derivamos la región. Es un movimiento programático
        // → el auto-switch por viewport no corre, por eso seteamos ambos a mano.
        let bestName = null, bestCode = null, bestDist = Infinity;
        for (const [name, info] of Object.entries(communesIndex || {})) {
          const b = info.bbox;
          if (!b || b.length !== 4) continue;
          if (longitude < b[0] || longitude > b[2] || latitude < b[1] || latitude > b[3]) continue;
          const [cx, cy] = info.centroid || [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
          const d = Math.hypot(longitude - cx, latitude - cy);
          if (d < bestDist) { bestDist = d; bestName = name; bestCode = info.region_code; }
        }
        if (bestName && bestCode != null) {
          lastZoomedRegion.current = bestCode;
          dispatch(setSelectedRegion(bestCode));
          lastZoomedCommune.current = bestName;
          dispatch(setSelectedCommune(bestName));
        } else {
          // Fallback (punto fuera de toda comuna conocida): región por bbox.
          const match = regions.find((r) => {
            const b = r.bbox;
            return b && longitude >= b[0] && longitude <= b[2]
                     && latitude >= b[1] && latitude <= b[3];
          });
          if (match) {
            lastZoomedRegion.current = match.code;
            dispatch(setSelectedRegion(match.code));
          }
        }
        flyTo({ longitude, latitude, zoom: GEOLOCATION_ZOOM, pitch: 0, bearing: 0 });
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 },
    );
  };

  // Auto-zoom al cambiar selectedRegion (dispara antes que el catalog).
  // Si el catalog de esa región ya está y trae bbox propio (más preciso),
  // usamos ese. Sino caemos al bbox hardcoded de regions[] (siempre presente).
  useEffect(() => {
    if (regions.length === 0) return;
    if (lastZoomedRegion.current === selectedRegion) return;
    const region = regions.find((r) => r.code === selectedRegion);
    const bbox =
      catalog?.region_code === selectedRegion && catalog.bbox?.length === 4
        ? catalog.bbox
        : region?.bbox;
    if (!bbox || bbox.length !== 4) return;

    const [minX, minY, maxX, maxY] = bbox;

    // Primera vez (ref aún null): si la URL trajo un viewport explícito y cae
    // dentro de la región, lo respetamos (deep-link deliberado) en vez de
    // recentrar. Si cae fuera (ej. defaults de Santiago con region=05), seguimos
    // y centramos en la región — que es lo que el usuario espera al abrir.
    if (lastZoomedRegion.current === null && urlViewport) {
      const { longitude, latitude } = urlViewport;
      const inside = longitude >= minX && longitude <= maxX
                  && latitude  >= minY && latitude  <= maxY;
      if (inside) { lastZoomedRegion.current = selectedRegion; return; }
    }

    const centerLon = (minX + maxX) / 2;
    const centerLat = (minY + maxY) / 2;
    const next = { ...viewState, longitude: centerLon, latitude: centerLat, zoom: DEFAULT_ZOOM };
    flyTo(next);
    lastZoomedRegion.current = selectedRegion;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, catalog, regions]);

  // Auto-switch de región al panear (deshabilitado en modo comparar).
  useEffect(() => {
    if (compareEnabled) return;
    if (!userInteracted.current) return;  // sólo en paneo real, no programático
    if (regions.length === 0 || !stableViewState) return;
    // A zoom muy lejano no auto-cambiamos de región (evita fetches al panear
    // por todo el país cuando ni siquiera se renderizan datos).
    if (stableViewState.zoom <= DATA_MIN_ZOOM) return;
    const { longitude, latitude } = stableViewState;
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return;
    const inside = (b) =>
      b && longitude >= b[0] && longitude <= b[2]
        && latitude  >= b[1] && latitude  <= b[3];
    const current = regions.find((r) => r.code === selectedRegion);
    if (inside(current?.bbox)) return;
    const next = regions.find((r) => inside(r.bbox));
    if (next && next.code !== selectedRegion) {
      lastZoomedRegion.current = next.code;
      dispatch(setSelectedRegion(next.code));
    }
  }, [stableViewState, regions, selectedRegion, compareEnabled, dispatch]);

  // Auto-switch de comuna al panear: encuentra la comuna del index
  // cuyo bbox contiene el centro del viewport y cuyo centroide está
  // más cerca. Pre-marca lastZoomedCommune para que el fitBounds no
  // re-encuadre (el usuario ya está mirando esa área).
  useEffect(() => {
    if (compareEnabled) return;
    if (!userInteracted.current) return;  // sólo en paneo real, no programático
    if (!communesIndex || !stableViewState) return;
    // A poco zoom mostramos la región completa: no auto-seleccionamos comuna
    // (la limpieza al alejar la hace el efecto de abajo).
    if (stableViewState.zoom < COMMUNE_MIN_ZOOM) return;
    const { longitude, latitude } = stableViewState;
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return;

    let best = null;
    let bestDist = Infinity;
    for (const [name, info] of Object.entries(communesIndex)) {
      if (info.region_code !== selectedRegion) continue;
      const b = info.bbox;
      if (!b || b.length !== 4) continue;
      if (longitude < b[0] || longitude > b[2]
          || latitude < b[1] || latitude > b[3]) continue;
      const [cx, cy] = info.centroid || [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
      const d = Math.hypot(longitude - cx, latitude - cy);
      if (d < bestDist) { bestDist = d; best = name; }
    }

    if (best && best !== selectedCommune) {
      lastZoomedCommune.current = best;
      dispatch(setSelectedCommune(best));
    }
  }, [stableViewState, communesIndex, selectedRegion, selectedCommune, compareEnabled, dispatch]);

  // Antes limpiábamos la comuna al alejar bajo COMMUNE_MIN_ZOOM, pero eso
  // disparaba en DataLoader un setGeojson(null) (sólo carga datos con comuna)
  // y los datos ya cargados desaparecían al hacer zoom out. Ahora la comuna
  // se mantiene: los datos cargados permanecen visibles a cualquier zoom.
  // Para volver a la vista de región el usuario limpia la comuna desde el
  // dropdown.

  // URL sync
  useEffect(() => {
    writeURL({
      region: selectedRegion,
      dateStart, dateEnd: dateEnd === dateStart ? null : dateEnd,
      timeStart: timeStart || null,
      timeEnd:   timeEnd   || null,
      cDateStart: compareEnabled ? compareDateStart : null,
      cDateEnd:   compareEnabled && compareDateEnd !== compareDateStart ? compareDateEnd : null,
      cTimeStart: compareEnabled ? compareTimeStart : null,
      cTimeEnd:   compareEnabled ? compareTimeEnd   : null,
      // Legacy params: limpiar para no confundir.
      date: null, hour: null, minute: null,
      commune: selectedCommune || null,
      jams: showJams ? null : "0", alerts: showAlerts ? null : "0",
    });
  }, [
    selectedRegion, dateStart, dateEnd, timeStart, timeEnd,
    compareEnabled, compareDateStart, compareDateEnd, compareTimeStart, compareTimeEnd,
    selectedCommune, showJams, showAlerts,
  ]);
  useEffect(() => {
    writeURL({
      lat: stableViewState.latitude.toFixed(4),
      lng: stableViewState.longitude.toFixed(4),
      zoom: stableViewState.zoom.toFixed(1),
    });
  }, [stableViewState]);

  // Swipe drag handlers (cortina). Listeners en window para soportar
  // arrastres que se salen del handle.
  useEffect(() => {
    if (!compareEnabled || compareMode !== "swipe") return;
    const onMove = (e) => {
      if (!swipeDragging.current || !swipeRef.current) return;
      const rect = swipeRef.current.getBoundingClientRect();
      const cx = e.clientX != null ? e.clientX : e.touches?.[0]?.clientX;
      if (cx == null) return;
      const pct = Math.max(0.02, Math.min(0.98, (cx - rect.left) / rect.width));
      setSwipePos(pct);
    };
    const onUp = () => { swipeDragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [compareEnabled, compareMode]);

  // RAF loop para animaciones (pulse de críticos, TripsLayer, cross-fade).
  // Throttle a ~30Hz para no incendiar la batería; el ojo no nota diferencia
  // con respecto a 60Hz en este tipo de efectos. Si cinematic está OFF,
  // no arranca el loop.
  useEffect(() => {
    if (!cinematic) return;
    let raf;
    let lastEmit = 0;
    const t0 = performance.now();
    const loop = (now) => {
      if (now - lastEmit >= 33) {
        const t = (now - t0) / 1000;
        tickRef.current = t;
        setTick(t);
        lastEmit = now;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [cinematic]);

  // Cross-fade entre horas: el cambio se dispara cuando llega data nueva
  // (no cuando cambia selectedHour, porque el reducer setea geojson=null
  // mientras carga). Comparamos referencias: si la nueva FeatureCollection
  // es distinta de la última conocida, gatillamos el fade.
  useEffect(() => {
    if (!geojson) return;
    if (lastGoodGeojson.current && lastGoodGeojson.current !== geojson) {
      prevHourGeojson.current = lastGoodGeojson.current;
      lastHourSwapAt.current = tickRef.current;
    }
    lastGoodGeojson.current = geojson;
  }, [geojson]);

  // Blink loop (alterna A/B cada 1.2s en modo blink).
  useEffect(() => {
    if (!compareEnabled || compareMode !== "blink" || !compareGeojson) {
      setBlinkSide("A");
      return;
    }
    const id = setInterval(() => {
      setBlinkSide((s) => (s === "A" ? "B" : "A"));
    }, 1200);
    return () => clearInterval(id);
  }, [compareEnabled, compareMode, compareGeojson]);

  // Timeline play — avanza la hora del rango (timeStart..timeEnd) en
  // ventanas de 1 hora. Solo aplica en single-day; rango multi-día deshabilita.
  useEffect(() => {
    if (!timelinePlaying || !isSingleDay) return;
    const tick = () => {
      const current = timeStart ? parseInt(timeStart.slice(0, 2), 10) : 0;
      const next = (current + 1) % 24;
      const hh = String(next).padStart(2, "0");
      dispatch(setTimeRange({ start: `${hh}:00`, end: `${hh}:59` }));
    };
    const id = setInterval(tick, 800);
    return () => clearInterval(id);
  }, [timelinePlaying, timeStart, isSingleDay, dispatch]);

  // ── Features + viewport/comuna filter ──────────────────────
  const allFeatures = useMemo(() => geojson?.features ?? [], [geojson]);
  const allAlertsFeatures = useMemo(() => alertsGeojson?.features ?? [], [alertsGeojson]);

  // Lista de comunas disponibles para el día actual, ordenadas alfabéticamente.
  // Si el communesIndex está cargado, lo usamos como fuente principal para
  // tener comunas estables aunque el día actual tenga pocos datos.
  // Opciones de comuna desde el índice ESTÁTICO (no desde los datos cargados):
  // como los datos no se cargan hasta elegir comuna, derivar el combo de los
  // datos sería un círculo vicioso (combo vacío → no se puede elegir → no carga).
  // El índice trae todas las comunas por región.
  const communeOptions = useMemo(() => {
    if (!communesIndex || !selectedRegion) return [];
    return Object.entries(communesIndex)
      .filter(([, info]) => String(info.region_code) === String(selectedRegion))
      .map(([name]) => ({ name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [communesIndex, selectedRegion]);

  // Filtro client-side por rango temporal. Semántica "ventana horaria
  // por día": para cada día en [dateStart, dateEnd] mostramos features
  // cuya hora cae en [timeStart, timeEnd] (no continuo). timeStart/End
  // null = día completo.
  const rangeFilter = useMemo(() => {
    const tsLo = timeStart ? `${timeStart}:00` : "00:00:00";
    const teHi = timeEnd   ? `${timeEnd}:59`   : "23:59:59";
    return (f) => {
      const d = f.properties?.date;
      const t = (f.properties?.time || "").replace(/-/g, ":");
      if (!d || !t) return false;
      if (d < dateStart || d > dateEnd) return false;
      if (t < tsLo || t > teHi) return false;
      return true;
    };
  }, [dateStart, dateEnd, timeStart, timeEnd]);

  // El geojson ya viene acotado por DataLoader (acumula sólo las comunas
  // visitadas de la región). No filtramos por selectedCommune aquí: así se
  // muestran todas las comunas cargadas, no sólo la última seleccionada.
  const features = useMemo(
    () => allFeatures.filter(rangeFilter),
    [allFeatures, rangeFilter],
  );

  const alertsFeatures = useMemo(
    () => allAlertsFeatures.filter(rangeFilter),
    [allAlertsFeatures, rangeFilter],
  );

  // Auto-zoom al cambiar selectedCommune.
  // Prioridad: 1) bbox precomputado del communes index (más estable);
  //            2) bbox de features filtradas del día (fallback).
  useEffect(() => {
    if (lastZoomedCommune.current === selectedCommune) return;
    lastZoomedCommune.current = selectedCommune;
    if (!selectedCommune) return;

    let bbox = null;
    // 1) Static index (varios días agregados, percentil 2-98).
    const idx = communesIndex?.[selectedCommune];
    if (idx?.bbox && idx.bbox.length === 4) {
      bbox = idx.bbox;
    } else if (features.length > 0) {
      // 2) Compute from current features.
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const f of features) {
        const g = f.geometry;
        if (!g) continue;
        if (g.type === "Point") {
          const [x, y] = g.coordinates;
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        } else if (g.type === "LineString") {
          for (const [x, y] of g.coordinates) {
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          }
        }
      }
      if (Number.isFinite(minX)) bbox = [minX, minY, maxX, maxY];
    }
    if (!bbox) return;

    try {
      const w = window.innerWidth, h = window.innerHeight;
      const vp = new WebMercatorViewport({ width: w, height: h, ...viewState });
      const fit = vp.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 60 });
      const next = {
        longitude: fit.longitude, latitude: fit.latitude,
        zoom: Math.min(Math.max(fit.zoom, COMMUNE_MIN_ZOOM), 16),
        pitch: 0, bearing: 0,
      };
      flyTo(next);
    } catch (e) { /* fitBounds puede fallar */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommune, features, communesIndex]);

  // Vuela el mapa al bbox de una comuna (por nombre). Usa el índice estático;
  // si no, cae al bbox de las features cargadas. Devuelve true si pudo.
  const flyToCommune = (name) => {
    if (!name) return false;
    let bbox = null;
    const idx = communesIndex?.[name];
    if (idx?.bbox && idx.bbox.length === 4) {
      bbox = idx.bbox;
    } else if (features.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const f of features) {
        const g = f.geometry;
        if (!g) continue;
        const coords = g.type === "Point" ? [g.coordinates] : (g.coordinates || []);
        for (const [x, y] of coords) {
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
      if (Number.isFinite(minX)) bbox = [minX, minY, maxX, maxY];
    }
    if (!bbox) return false;
    try {
      const vp = new WebMercatorViewport({ width: window.innerWidth, height: window.innerHeight, ...viewState });
      const fit = vp.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding: 60 });
      lastZoomedCommune.current = name;
      flyTo({
        longitude: fit.longitude, latitude: fit.latitude,
        // Nunca por debajo de COMMUNE_MIN_ZOOM: si no, el efecto de "limpiar
        // comuna al alejar" la borraría y quedaría sin datos.
        zoom: Math.min(Math.max(fit.zoom, COMMUNE_MIN_ZOOM), 16), pitch: 0, bearing: 0,
      });
      return true;
    } catch (e) { return false; }
  };

  // Vuela el mapa al centro de una región (por código). Devuelve true si pudo.
  const flyToRegion = (code) => {
    if (!code) return false;
    const region = regions.find((r) => r.code === code);
    const bbox = (catalog?.region_code === code && catalog.bbox?.length === 4)
      ? catalog.bbox : region?.bbox;
    if (!bbox || bbox.length !== 4) return false;
    const [minX, minY, maxX, maxY] = bbox;
    lastZoomedRegion.current = code;
    flyTo({
      ...viewState,
      longitude: (minX + maxX) / 2, latitude: (minY + maxY) / 2, zoom: DEFAULT_ZOOM,
    });
    return true;
  };


  // Bbox del viewport con padding 25% — así panear ligeramente NO invalida
  // el subset visible (evita re-tessellation constante en deck.gl).
  const visibleBbox = useMemo(() => {
    try {
      const w = window.innerWidth, h = window.innerHeight;
      const vp = new WebMercatorViewport({ width: w, height: h, ...stableViewState });
      const [minX, minY, maxX, maxY] = vp.getBounds();
      const padX = (maxX - minX) * 0.25;
      const padY = (maxY - minY) * 0.25;
      return [minX - padX, minY - padY, maxX + padX, maxY + padY];
    } catch { return null; }
  }, [stableViewState]);

  const filterByBbox = (feats, bbox) => {
    if (!bbox || !feats?.length) return feats || [];
    const [minX, minY, maxX, maxY] = bbox;
    return feats.filter((f) => {
      const g = f.geometry;
      if (!g) return false;
      if (g.type === "Point") {
        const [lon, lat] = g.coordinates;
        return lon >= minX && lon <= maxX && lat >= minY && lat <= maxY;
      }
      if (g.type === "LineString") {
        return g.coordinates.some(([lon, lat]) =>
          lon >= minX && lon <= maxX && lat >= minY && lat <= maxY);
      }
      return true;
    });
  };

  const visibleJams = useMemo(() => filterByBbox(features, visibleBbox), [features, visibleBbox]);
  const visibleAlerts = useMemo(() => filterByBbox(alertsFeatures, visibleBbox), [alertsFeatures, visibleBbox]);

  // Markers de alertas (dedup + declutter por zoom) para el período A.
  const alertMarkers = useMemo(
    () => declutterAlerts(visibleAlerts, stableViewState.zoom, stableViewState.latitude),
    [visibleAlerts, stableViewState.zoom, stableViewState.latitude],
  );
  // Idem para el período B (comparativa): se dibujan en el lado B del mapa.
  const alertMarkersB = useMemo(() => {
    if (!compareEnabled) return [];
    let feats = compareAlertsGeojson?.features ?? [];
    if (selectedCommune) feats = feats.filter((f) => f.properties?.city === selectedCommune);
    const vis = filterByBbox(feats, visibleBbox);
    return declutterAlerts(vis, stableViewState.zoom, stableViewState.latitude);
  }, [compareEnabled, compareAlertsGeojson, selectedCommune, visibleBbox, stableViewState.zoom, stableViewState.latitude]);

  // ── Layers ──────────────────────────────────────────────────
  // FeatureCollections para las layers. Usamos los subsets filtrados por
  // viewport (con padding) para que deck.gl sólo tessellate lo cercano —
  // gran win de GPU cuando el rango trae 30k+ features pero estás zoomeado
  // a una comuna.
  const data = useMemo(
    () => ({ type: "FeatureCollection", features: visibleJams }),
    [visibleJams],
  );
  // compareGeojson aún sin viewport-filter (TODO próxima iteración).
  const dataCompare = compareGeojson || { type: "FeatureCollection", features: [] };

  // A zoom bajo (<12) las líneas saturan y no se entiende dónde está
  // la congestión. Pasamos a un HeatmapLayer alimentado por centroides
  // ponderados por level, y a zoom ≥ 12 volvemos al detalle de líneas.
  const useHeatmap = stableViewState.zoom < 12;

  // Centroides de cada LineString para el heatmap (point cloud con weight).
  // Usamos visibleJams (subset del viewport) en vez del rango completo:
  // la GPU no se ve afectada porque el heatmap es bin-based, pero igual
  // ahorramos en el for-loop y el binning.
  const heatmapPoints = useMemo(() => {
    if (!visibleJams?.length) return [];
    const pts = [];
    for (const f of visibleJams) {
      const g = f.geometry;
      if (!g || g.type !== "LineString") continue;
      const cs = g.coordinates;
      if (!cs || cs.length === 0) continue;
      // Usamos el punto medio del LineString para ubicar el "peso".
      const mid = cs[Math.floor(cs.length / 2)];
      pts.push({
        position: mid,
        weight: 1 + (Number(f.properties?.level) || 0),
      });
    }
    return pts;
  }, [visibleJams]);

  // Features con z = level*12m para modo 3D. Lo computamos sólo cuando
  // is3D está activo para no copiar coords innecesariamente. Usa el subset
  // del viewport — sin sentido elevar features que no se renderizan.
  const data3D = useMemo(() => {
    if (!is3D || !visibleJams?.length) return data;
    return {
      type: "FeatureCollection",
      features: visibleJams.map((f) => {
        if (f.geometry?.type !== "LineString") return f;
        const lvl = Number(f.properties?.level) || 0;
        const z = lvl * 18; // 18m por nivel — visible sin invadir
        return {
          ...f,
          geometry: {
            ...f.geometry,
            coordinates: f.geometry.coordinates.map((c) => [c[0], c[1], z]),
          },
        };
      }),
    };
  }, [is3D, visibleJams, data]);

  // tripsData: derivado de features. Para cada LineString computamos
  // longitud acumulada por vértice y la normalizamos a [0, TRIPS_LOOP_MS]
  // así todas las calles completan un ciclo simultáneo (visualmente más
  // coherente que usar speed real, que haría tramos cortos parpadear).
  // El nivel y el speedKMH se preservan para color y width. En 3D los
  // path coords incluyen z = level*18m para que el trail flote a la misma
  // altura que la línea base y no parezca rastreando por debajo.
  const tripsData = useMemo(() => {
    if (!cinematic || !visibleJams?.length) return [];
    const trips = [];
    for (const f of visibleJams) {
      const g = f.geometry;
      if (!g || g.type !== "LineString") continue;
      const coords = g.coordinates;
      if (!coords || coords.length < 2) continue;
      const lvl = Number(f.properties?.level) || 0;
      const path = is3D ? coords.map((c) => [c[0], c[1], lvl * 18]) : coords;
      const arcs = [0];
      for (let i = 1; i < coords.length; i++) {
        const [x1, y1] = coords[i - 1];
        const [x2, y2] = coords[i];
        const dx = (x2 - x1) * 111000 * Math.cos((y1 * Math.PI) / 180);
        const dy = (y2 - y1) * 111000;
        arcs.push(arcs[i - 1] + Math.sqrt(dx * dx + dy * dy));
      }
      const total = arcs[arcs.length - 1] || 1;
      const timestamps = arcs.map((a) => (a / total) * TRIPS_LOOP_MS);
      trips.push({
        path,
        timestamps,
        level: lvl,
        speedKMH: f.properties?.speedKMH || 0,
      });
    }
    return trips;
  }, [visibleJams, cinematic, is3D]);

  // Delta por calle entre A (selectedDate) y B (compareDate). Promedia
  // `level` por calle en cada lado y devuelve { delta, kind } por nombre.
  // kind: 'both' | 'a-only' | 'b-only'. delta = avg(B) - avg(A) cuando
  // ambos existen (positivo = empeoró en B).
  const streetDeltas = useMemo(() => {
    if (!compareEnabled || !compareGeojson) return {};
    const accumA = {}; const accumB = {};
    for (const f of features) {
      const s = f.properties?.street; if (!s) continue;
      const l = Number(f.properties?.level) || 0;
      if (!accumA[s]) accumA[s] = { sum: 0, n: 0 };
      accumA[s].sum += l; accumA[s].n += 1;
    }
    for (const f of (compareGeojson.features || [])) {
      const s = f.properties?.street; if (!s) continue;
      // Filtrar por comuna seleccionada (B usa el mismo filtro que A).
      if (selectedCommune && f.properties?.city !== selectedCommune) continue;
      const l = Number(f.properties?.level) || 0;
      if (!accumB[s]) accumB[s] = { sum: 0, n: 0 };
      accumB[s].sum += l; accumB[s].n += 1;
    }
    const out = {};
    const all = new Set([...Object.keys(accumA), ...Object.keys(accumB)]);
    for (const s of all) {
      const a = accumA[s] ? accumA[s].sum / accumA[s].n : null;
      const b = accumB[s] ? accumB[s].sum / accumB[s].n : null;
      if (a === null) out[s] = { kind: "b-only", delta: 0, a, b };
      else if (b === null) out[s] = { kind: "a-only", delta: 0, a, b };
      else out[s] = { kind: "both", delta: b - a, a, b };
    }
    return out;
  }, [compareEnabled, compareGeojson, features, selectedCommune]);

  // Color del diff por feature según el delta de su calle.
  // Convención: rojo = empeoró en B, verde = mejoró en B, gris = ≈ igual,
  // azul = sólo aparece en B, naranja = sólo en A.
  const DIFF_COLOR = {
    worse:   [215, 19, 7, 230],
    better:  [40, 180, 70, 230],
    same:    [150, 150, 150, 180],
    aOnly:   [255, 123, 2, 220],
    bOnly:   [80, 130, 255, 220],
    unknown: [120, 120, 120, 140],
  };
  const getDiffColor = (f) => {
    const s = f.properties?.street;
    if (!s) return DIFF_COLOR.unknown;
    const d = streetDeltas[s];
    if (!d) return DIFF_COLOR.unknown;
    if (d.kind === "a-only") return DIFF_COLOR.aOnly;
    if (d.kind === "b-only") return DIFF_COLOR.bOnly;
    if (d.delta > 0.5) return DIFF_COLOR.worse;
    if (d.delta < -0.5) return DIFF_COLOR.better;
    return DIFF_COLOR.same;
  };
  const diffData = useMemo(() => {
    if (!compareEnabled || !compareGeojson) {
      return { type: "FeatureCollection", features: [] };
    }
    const bFeats = (compareGeojson.features || []).filter(
      (f) => !selectedCommune || f.properties?.city === selectedCommune,
    );
    return { type: "FeatureCollection", features: [...features, ...bFeats] };
  }, [compareEnabled, compareGeojson, features, selectedCommune]);

  // Construye layers para un lado dado. side ∈ {'A','B','overlay','diff'}.
  // 'A' = sólo capa de la fecha principal (jams + alerts + highlight).
  // 'B' = sólo capa de la fecha de comparación (jams con misma paleta).
  // 'overlay' = comportamiento histórico: A + B (B en azul).
  // 'diff' = capa única coloreada por delta por calle.
  const buildLayers = (side) => {
    const layers = [];

    // A zoom muy lejano no cargamos/renderizamos datos (jams, alertas, bordes
    // de comunas): a escala país/continente es pesado e ilegible. Sólo basemap.
    if (stableViewState.zoom <= DATA_MIN_ZOOM) return layers;

    // Hover con etiqueta de lado: en split/swipe el tooltip se renderiza en el
    // canvas que corresponde (A o B), porque las coords son relativas a cada uno.
    const setHover = (info) =>
      setHoverInfo(info?.object ? { ...info, _side: side === "B" ? "B" : "A" } : null);

    // Helper: capa de íconos de alertas (accidentes/incidentes) para un set de
    // markers ya deduplicados/decluttered. Se usa para A y para B.
    const pushAlerts = (data, id) => {
      if (!showAlerts || !data?.length) return;
      layers.push(
        new IconLayer({
          id, data, pickable: true,
          getIcon: getAlertIcon,
          getPosition: (f) => f.geometry?.coordinates || [0, 0],
          getSize: 38, sizeMinPixels: 28, sizeMaxPixels: 58,
          onHover: setHover,
          onClick: (info) => dispatch(setSelectedFeature(info.object || null)),
          updateTriggers: { getIcon: [data] },
        }),
      );
    };

    if (side === "diff" && compareEnabled && compareGeojson) {
      layers.push(
        new GeoJsonLayer({
          id: "diff-glow", data: diffData, stroked: true, pickable: false,
          getLineColor: (f) => {
            const c = getDiffColor(f);
            return [c[0], c[1], c[2], 60];
          },
          getLineWidth: (f) => f.properties?.speedKMH < 5 ? 18 : f.properties?.speedKMH < 20 ? 14 : 10,
          lineWidthMinPixels: 5,
          updateTriggers: { getLineColor: [streetDeltas] },
        }),
        new GeoJsonLayer({
          id: "diff-line", data: diffData, stroked: true, pickable: true,
          getLineColor: getDiffColor,
          getLineWidth: (f) => f.properties?.speedKMH < 5 ? 8 : f.properties?.speedKMH < 20 ? 6 : 4,
          lineWidthMinPixels: 2,
          onHover: setHover,
          onClick: (info) => dispatch(setSelectedFeature(info.object || null)),
          updateTriggers: { getLineColor: [streetDeltas] },
        }),
      );
      pushAlerts(alertMarkers, "alerts-diff");
      return layers;
    }

    // B en modo overlay (azul, para distinguir de A en mismo canvas)
    if (side === "overlay" && compareEnabled && compareGeojson) {
      // Las líneas de B usan la MISMA paleta de congestión que A (rojo→verde);
      // la distinción A/B se hace por el color del mapa base, no por las líneas.
      layers.push(
        new GeoJsonLayer({
          id: "compare-glow", data: dataCompare, stroked: true, pickable: false,
          getLineColor: (f) => getGlowColor(f.properties?.level),
          getLineWidth: (f) => f.properties?.speedKMH < 5 ? 18 : f.properties?.speedKMH < 20 ? 14 : 10,
          lineWidthMinPixels: 4,
        }),
        new GeoJsonLayer({
          id: "compare-line", data: dataCompare, stroked: true, pickable: true,
          getLineColor: (f) => getColorByLevel(f.properties?.level),
          getLineWidth: (f) => f.properties?.speedKMH < 5 ? 8 : f.properties?.speedKMH < 20 ? 6 : 4,
          lineWidthMinPixels: 2,
          onClick: (info) => dispatch(setSelectedFeature(info.object || null)),
        }),
      );
    }

    // B solo (split/swipe/blink): misma paleta de congestión que A. La distinción
    // se hace con un basemap CLARO (positron) en el lado B vs el oscuro de A
    // (ver BASEMAP_STYLE_B en este archivo).
    if (side === "B" && compareEnabled && compareGeojson) {
      layers.push(
        new GeoJsonLayer({
          id: "b-glow", data: dataCompare, stroked: true, pickable: false,
          getLineColor: (f) => getGlowColor(f.properties?.level),
          getLineWidth: (f) => f.properties?.speedKMH < 5 ? 18 : f.properties?.speedKMH < 20 ? 14 : 10,
          lineWidthMinPixels: 5,
        }),
        new GeoJsonLayer({
          id: "b-line", data: dataCompare, stroked: true, pickable: true,
          getLineColor: (f) => getColorByLevel(f.properties?.level),
          getLineWidth: (f) => f.properties?.speedKMH < 5 ? 8 : f.properties?.speedKMH < 20 ? 6 : 4,
          lineWidthMinPixels: 2,
          onHover: setHover,
          onClick: (info) => dispatch(setSelectedFeature(info.object || null)),
        }),
      );
      pushAlerts(alertMarkersB, "alerts-b");
    }

    // A (jams) — para overlay, A, split-A, swipe-A, blink cuando blinkSide==='A'.
    const showA = side === "A" || side === "overlay";

    // Heatmap a zoom bajo: reemplaza las jams-line por densidad.
    // No aplica en modo diff/B/etc — sólo cuando A es la fuente.
    if (showA && showJams && geojson && useHeatmap && heatmapPoints.length > 0) {
      layers.push(
        new HeatmapLayer({
          id: "jams-heatmap",
          data: heatmapPoints,
          getPosition: (d) => d.position,
          getWeight: (d) => d.weight,
          // Paleta verde→amarillo→rojo para alinear con la semántica del visor.
          colorRange: [
            [40, 180, 70, 80],
            [255, 204, 1, 120],
            [255, 173, 3, 170],
            [255, 123, 2, 200],
            [215, 19, 7, 220],
            [120, 10, 4, 230],
          ],
          radiusPixels: 60,
          intensity: 1.4,
          threshold: 0.04,
          aggregation: "SUM",
          pickable: false,
        }),
      );
    }

    if (showA && showJams && geojson && !useHeatmap) {
      // Pulse factor para críticos (level ≥4). Período ~1.5s, amplitud 20%.
      // Si cinematic está OFF lo dejamos en 1 (sin pulso, sin re-render).
      const pulseFactor = (f) => {
        const lvl = f.properties?.level || 0;
        if (!cinematic || lvl < 4) return 1;
        return 1 + 0.22 * Math.sin(tick * 4 + (f.properties?.uuid?.length || 0) * 0.3);
      };
      const baseWidth = (f) => f.properties?.speedKMH < 5 ? 8 : f.properties?.speedKMH < 20 ? 6 : 4;
      const baseGlowWidth = (f) => f.properties?.speedKMH < 5 ? 18 : f.properties?.speedKMH < 20 ? 14 : 10;
      // Cross-fade: si hubo swap de hora reciente, fade-in del actual.
      const swapAge = tick - lastHourSwapAt.current;
      const fadeDuration = 0.6;
      const fadeIn = cinematic && swapAge >= 0 && swapAge < fadeDuration
        ? Math.min(1, swapAge / fadeDuration)
        : 1;
      const tickKey = cinematic ? Math.floor(tick * 30) : 0;

      // Capa anterior fading-out (si hubo swap reciente y tenemos prev).
      // En 3D elevamos también el prev para que no quede "rastreando" en
      // el plano mientras la capa actual flota en su nivel.
      if (cinematic && swapAge < fadeDuration && prevHourGeojson.current) {
        const fadeOutAlpha = 1 - fadeIn;
        const prevRaw = prevHourGeojson.current;
        const prevData = is3D ? {
          type: "FeatureCollection",
          features: (prevRaw.features || []).map((f) => {
            if (f.geometry?.type !== "LineString") return f;
            const lvl = Number(f.properties?.level) || 0;
            return {
              ...f,
              geometry: {
                ...f.geometry,
                coordinates: f.geometry.coordinates.map((c) => [c[0], c[1], lvl * 18]),
              },
            };
          }),
        } : prevRaw;
        layers.push(
          new GeoJsonLayer({
            id: "jams-line-fadeout",
            data: prevData, stroked: true, pickable: false,
            getLineColor: (f) => {
              const c = getColorByLevel(f.properties?.level);
              return [c[0], c[1], c[2], Math.round(c[3] * fadeOutAlpha)];
            },
            getLineWidth: baseWidth,
            lineWidthMinPixels: 2,
            updateTriggers: { getLineColor: [tickKey], getPath: [is3D] },
          }),
        );
      }

      layers.push(
        new GeoJsonLayer({
          id: "jams-glow", data: data3D, stroked: true, pickable: false,
          getLineColor: (f) => {
            const c = getGlowColor(f.properties?.level);
            const p = pulseFactor(f);
            // En críticos el glow se intensifica con el pulso
            const alpha = Math.round(c[3] * (p > 1 ? 1 + (p - 1) * 1.5 : 1) * fadeIn);
            return [c[0], c[1], c[2], Math.min(255, alpha)];
          },
          getLineWidth: (f) => baseGlowWidth(f) * pulseFactor(f),
          lineWidthMinPixels: 5,
          updateTriggers: {
            getLineColor: [features.length, tickKey, is3D],
            getLineWidth: [features.length, tickKey, is3D],
          },
        }),
        new GeoJsonLayer({
          id: "jams-line", data: data3D, stroked: true, pickable: true,
          getLineColor: (f) => {
            const c = getColorByLevel(f.properties?.level);
            return [c[0], c[1], c[2], Math.round(c[3] * fadeIn)];
          },
          getLineWidth: (f) => baseWidth(f) * pulseFactor(f),
          lineWidthMinPixels: 2,
          onHover: setHover,
          onClick: (info) => dispatch(setSelectedFeature(info.object || null)),
          updateTriggers: {
            getLineColor: [features.length, tickKey, is3D],
            getLineWidth: [features.length, tickKey, is3D],
          },
        }),
      );

      // TripsLayer: trail animado fluyendo por cada calle.
      // Sólo cuando cinematic está ON, porque sintetizar timestamps + redraw
      // por frame es lo más caro de los tres efectos.
      if (cinematic && tripsData.length > 0) {
        // currentTime loopeado en un ciclo común de TRIPS_LOOP segundos.
        const currentTime = (tick * 1000) % TRIPS_LOOP_MS;
        layers.push(
          new TripsLayer({
            id: "jams-trips",
            data: tripsData,
            getPath: (d) => d.path,
            getTimestamps: (d) => d.timestamps,
            getColor: (d) => {
              const c = getColorByLevel(d.level);
              return [Math.min(255, c[0] + 40), Math.min(255, c[1] + 40), Math.min(255, c[2] + 40)];
            },
            opacity: 0.85,
            widthMinPixels: 2.5,
            getWidth: (d) => d.level >= 4 ? 4 : 3,
            rounded: true,
            trailLength: TRIPS_LOOP_MS * 0.35,
            currentTime,
            pickable: false,
          }),
        );
      }
    }

    if (showA) {
      pushAlerts(alertMarkers, "alerts-a");
    }
    // Highlight: features de la calle seleccionada en el bar chart.
    if (showA && selectedStreet && features.length > 0) {
      const highlightFeatures = features.filter(
        (f) => f.properties?.street === selectedStreet,
      );
      const highlightData = { type: "FeatureCollection", features: highlightFeatures };
      layers.push(
        new GeoJsonLayer({
          id: "street-highlight-glow", data: highlightData, stroked: true, pickable: false,
          getLineColor: [255, 255, 255, 230],
          getLineWidth: 22, lineWidthMinPixels: 10,
        }),
        new GeoJsonLayer({
          id: "street-highlight-line", data: highlightData, stroked: true, pickable: true,
          getLineColor: [80, 130, 255, 240],
          getLineWidth: 10, lineWidthMinPixels: 4,
          onClick: (info) => dispatch(setSelectedFeature(info.object || null)),
        }),
      );
    }
    return layers;
  };

  // Selector del lado a renderizar en el canvas principal según el modo.
  const mainSide = !compareEnabled
    ? "A"
    : compareMode === "diff"
      ? "diff"
      : compareMode === "blink"
        ? (blinkSide === "B" ? "B" : "A")
        : compareMode === "overlay"
          ? "overlay"
          : "A"; // split/swipe: el canvas principal muestra A
  const mainLayers = buildLayers(mainSide);
  // Para split/swipe: layers del lado B en un canvas separado.
  const bLayers = (compareEnabled && (compareMode === "split" || compareMode === "swipe"))
    ? buildLayers("B")
    : [];

  // ── KPIs ────────────────────────────────────────────────────
  const kpis = useMemo(
    () => computeKpis(visibleJams, visibleAlerts, features.length, alertsFeatures.length, catalog),
    [visibleJams, visibleAlerts, features, alertsFeatures, catalog],
  );

  // ── KPIs del período B (comparativa) ───────────────────────
  // Mismas métricas que A pero sobre los datos de B, filtrados por la misma
  // comuna y viewport para que sean comparables. null si no se compara.
  const featuresB = useMemo(() => {
    const feats = compareGeojson?.features ?? [];
    return selectedCommune ? feats.filter((f) => f.properties?.city === selectedCommune) : feats;
  }, [compareGeojson, selectedCommune]);
  const alertsFeaturesB = useMemo(() => {
    const feats = compareAlertsGeojson?.features ?? [];
    return selectedCommune ? feats.filter((f) => f.properties?.city === selectedCommune) : feats;
  }, [compareAlertsGeojson, selectedCommune]);
  const visibleJamsB = useMemo(() => filterByBbox(featuresB, visibleBbox), [featuresB, visibleBbox]);
  const visibleAlertsB = useMemo(() => filterByBbox(alertsFeaturesB, visibleBbox), [alertsFeaturesB, visibleBbox]);
  const kpisB = useMemo(() => {
    if (!compareEnabled || !compareGeojson) return null;
    return computeKpis(visibleJamsB, visibleAlertsB, featuresB.length, alertsFeaturesB.length, compareCatalog);
  }, [compareEnabled, compareGeojson, visibleJamsB, visibleAlertsB, featuresB, alertsFeaturesB, compareCatalog]);

  // ¿Hay datos de gravedad de B? (sólo si la capa de alertas está activa y B
  // cargó alertas). Si no, mostramos "—" en vez de un Δ engañoso vs 0.
  const hasBGravity = compareEnabled && showAlerts && !!compareAlertsGeojson;

  // Conteo de atascos por calle para B (deduplicado), para comparar el bar
  // chart de ejes principales contra las mismas calles de A.
  const bStreetCounts = useMemo(() => {
    if (!kpisB) return null;
    const seen = new Set();
    const counts = {};
    for (const f of visibleJamsB) {
      const p = f.properties || {};
      const key = p.uuid || `${p.street}|${(f.geometry?.coordinates?.[0] || []).join(",")}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (p.street) counts[p.street] = (counts[p.street] || 0) + 1;
    }
    return counts;
  }, [kpisB, visibleJamsB]);

  // Veredicto global: variación del nivel de congestión promedio A vs B.
  const compareVerdict = useMemo(() => {
    if (!kpisB) return null;
    const a = kpis.avg_level, b = kpisB.avg_level;
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
    const pct = ((a - b) / b) * 100;
    return { pct, dir: pct > 1 ? "up" : pct < -1 ? "down" : "flat" };
  }, [kpis.avg_level, kpisB]);

  // ── Helpers de mapa (compartidos entre canvases) ───────────
  const BASEMAP_STYLE = process.env.REACT_APP_BASEMAP_STYLE
    || "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
  // Basemap CLARO (positron) para el lado B del comparativo. La distinción A/B
  // se hace usando un mapa realmente claro en B (vs el oscuro dark-matter de A),
  // en vez de un filtro CSS sobre el mapa oscuro (que quedaba lavado).
  const BASEMAP_STYLE_B = process.env.REACT_APP_BASEMAP_STYLE_B
    || "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

  const onSharedViewStateChange = ({ viewState: v }) => {
    userInteracted.current = true;
    const clamped = { ...v, zoom: Math.min(Math.max(v.zoom, 5), 20) };
    setViewState(clamped);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setStableViewState(clamped), 200);
  };

  // Helper para el tooltip: features de B sobre la misma calle
  // que la feature que el usuario está hovereando en A.
  const compareSameStreet = (hoverFeature) => {
    if (!compareEnabled || !compareGeojson?.features) return null;
    const street = hoverFeature?.properties?.street;
    if (!street) return null;
    return compareGeojson.features.filter((f) => f.properties?.street === street);
  };
  // Inverso: features de A sobre la misma calle (para el tooltip del lado B,
  // donde la feature hovereada es de B y se compara contra A).
  const sameStreetA = (hoverFeature) => {
    const street = hoverFeature?.properties?.street;
    if (!street) return null;
    return features.filter((f) => f.properties?.street === street);
  };

  // ── Hour bars ───────────────────────────────────────────────
  const hoursBars = useMemo(() => {
    const arr = [];
    for (let h = 0; h < 24; h++) {
      const hh = String(h).padStart(2, "0");
      arr.push({
        hh,
        jams: catalog?.hours?.[hh]?.feature_count ?? 0,
        alerts: catalog?.alerts_hours?.[hh]?.feature_count ?? 0,
      });
    }
    return arr;
  }, [catalog]);
  // Actividad por hora del período B (comparativa). null si no se compara.
  const hoursBarsB = useMemo(() => {
    if (!compareEnabled || !compareCatalog) return null;
    const arr = [];
    for (let h = 0; h < 24; h++) {
      const hh = String(h).padStart(2, "0");
      arr.push({ jams: compareCatalog?.hours?.[hh]?.feature_count ?? 0 });
    }
    return arr;
  }, [compareEnabled, compareCatalog]);
  // Max compartido entre A y B para que las alturas sean comparables.
  const maxJams = Math.max(1, ...hoursBars.map((b) => b.jams), ...(hoursBarsB?.map((b) => b.jams) ?? []));
  const maxAlerts = Math.max(1, ...hoursBars.map((b) => b.alerts));

  // ── Bar chart top streets ───────────────────────────────────

  const topStreetsData = useMemo(() => {
    const streets = kpis.topStreets;
    const labels = streets.map((s) => (s.name.length > 28 ? s.name.slice(0, 26) + "…" : s.name));
    const cmp = !!(kpisB && bStreetCounts);
    const datasets = [{
      label: "A",
      data: streets.map((s) => s.count),
      backgroundColor: "#f5c518",
      borderRadius: 2,
      barThickness: cmp ? 7 : 12,
    }];
    if (cmp) {
      datasets.push({
        label: "B",
        data: streets.map((s) => bStreetCounts[s.name] || 0),
        backgroundColor: "#06b6d4",
        borderRadius: 2,
        barThickness: 7,
      });
    }
    return { labels, datasets };
  }, [kpis.topStreets, kpisB, bStreetCounts]);

  return (
    <>
      <DataLoader />

      {showWelcome && (
        <WelcomeModal
          onClose={() => setShowWelcome(false)}
          onGeolocate={() => { setShowWelcome(false); geolocateMe(); }}
        />
      )}

      {/* Shell unificado: iconbar + panel comparten contenedor */}
      <div ref={shellRef} className={`clbb-shell ${collapsed ? "clbb-collapsed" : ""}`}>
        <div className="clbb-iconbar"
             onMouseEnter={() => { if (autoCollapse && collapsed) setCollapsed(false); }}>
          <button
            className={`clbb-iconbar-btn ${!collapsed ? "active" : ""}`}
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Mostrar filtros" : "Ocultar filtros"}>
            <span className="material-icons">tune</span>
          </button>
          <button
            className={`clbb-iconbar-btn ${showJams ? "active" : ""}`}
            onClick={() => dispatch(setShowJams(!showJams))}
            title={showJams ? "Ocultar atascos" : "Mostrar atascos"}>
            <span className="material-icons">directions_car</span>
          </button>
          <button
            className={`clbb-iconbar-btn ${showAlerts ? "active" : ""}`}
            onClick={() => dispatch(setShowAlerts(!showAlerts))}
            title={showAlerts ? "Ocultar alertas" : "Mostrar alertas"}>
            <span className="material-icons">warning</span>
          </button>
          <button
            className={`clbb-iconbar-btn ${cinematic ? "active" : ""}`}
            onClick={() => setCinematic((c) => !c)}
            title={cinematic ? "Apagar animaciones" : "Encender animaciones"}>
            <span className="material-icons">{cinematic ? "auto_awesome" : "auto_awesome_motion"}</span>
          </button>
          <button
            className={`clbb-iconbar-btn ${is3D ? "active" : ""}`}
            onClick={() => {
              const next = !is3D;
              setIs3D(next);
              // Animar el pitch al entrar/salir de 3D vía deck.gl transition.
              setViewState((v) => ({
                ...v,
                pitch: next ? 50 : 0,
                transitionDuration: 600,
              }));
            }}
            title={is3D ? "Vista 2D" : "Vista 3D"}>
            <span className="material-icons">{is3D ? "map" : "view_in_ar"}</span>
          </button>
          {isLoading && (
            <div className="clbb-iconbar-loading" title="Cargando">
              <span className="material-icons clbb-spin">refresh</span>
            </div>
          )}
        </div>

        {/* Toggle drawer en mobile (solo cuando panel collapsed) */}

        {/* Panel principal de filtros */}
        <div className="clbb-panel">
          <div className="clbb-panel-inner">
          {/* Logo */}
          <img className="clbb-logo" src={visorLogo} alt="Visor de tráfico y accidentes" />
          {/* Header */}
          <div className="clbb-h2">
            <span><span className="clbb-brand-dot" />Filtros de información</span>
            <button className="clbb-close" onClick={() => setCollapsed(true)} title="Ocultar panel">
              <span className="material-icons">chevron_left</span>
            </button>
          </div>
          <label className="clbb-checkbox clbb-autocollapse"
                 title="Oculta el panel al hacer clic en el mapa; pase el cursor por la barra de íconos para volver a mostrarlo">
            <input type="checkbox" checked={autoCollapse}
                   onChange={(e) => setAutoCollapse(e.target.checked)} />
            <span>Auto-ocultar al hacer clic fuera</span>
          </label>

          {/* Región + comuna dependiente (deshabilitadas en modo comparar) */}
          <div className="clbb-row">
            <div className="clbb-col">
              <div className="clbb-label">
                Región
                {compareEnabled && (
                  <span className="material-icons clbb-locked">lock</span>
                )}
              </div>
              <SearchSelect
                value={selectedRegion}
                onChange={(v) => {
                  // Cambia la región y vuela allí. flyTo marca el movimiento como
                  // programático (userInteracted=false) para que el auto-switch
                  // por viewport no revierta la selección al re-ejecutarse.
                  dispatch(setSelectedRegion(v));
                  flyToRegion(v);
                }}
                options={regions.map((r) => ({ value: r.code, label: r.name }))}
                placeholder="Elegir región…"
                searchPlaceholder="Buscar región…"
                disabled={compareEnabled}
              />
            </div>
            <div className="clbb-col">
              <div className="clbb-label">
                Comuna
                {compareEnabled && (
                  <span className="material-icons clbb-locked">lock</span>
                )}
              </div>
              <SearchSelect
                value={selectedCommune || ""}
                onChange={(v) => {
                  dispatch(setSelectedCommune(v || null));
                  if (v) flyToCommune(v);
                  else lastZoomedCommune.current = null;
                }}
                disabled={compareEnabled || communeOptions.length === 0}
                options={[
                  { value: "", label: "Todas" },
                  ...communeOptions.map(({ name }) => ({ value: name, label: name })),
                ]}
                placeholder="Todas"
                searchPlaceholder="Buscar comuna…"
                emptyText="Sin comunas con datos"
              />
            </div>
          </div>
          {compareEnabled && (
            <div className="clbb-compare-hint">
              Comparando misma región/comuna entre dos fechas. Desactive el
              comparador para cambiar de área.
            </div>
          )}

          {/* Rango: chips de presets + resumen + pickers en "Personalizado" */}
          <div className="clbb-range-presets">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`clbb-chip ${activePresetId === p.id ? "active" : ""}`}
                onClick={() => {
                  userPickedTime.current = true;
                  dispatch(setTimelinePlaying(false));
                  const r = p.range();
                  dispatch(setDateRange({ start: r.dateStart, end: r.dateEnd }));
                  dispatch(setTimeRange({ start: r.timeStart, end: r.timeEnd }));
                  setCustomExpanded(false);
                }}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              className={`clbb-chip ${activePresetId === null || customExpanded ? "active" : ""}`}
              onClick={() => setCustomExpanded((v) => !v)}
              title="Editar fecha y hora manualmente"
            >
              Personalizado{customExpanded ? " ▾" : " ▸"}
            </button>
          </div>
          <div className="clbb-range-summary">
            {fmtDayMonth(dateStart)}
            {timeStart && <> · {timeStart}</>}
            {(dateStart !== dateEnd || (timeEnd && timeEnd !== timeStart)) && (
              <>
                <span className="clbb-range-arrow"> → </span>
                {fmtDayMonth(dateEnd)}
                {timeEnd && <> · {timeEnd}</>}
              </>
            )}
          </div>
          {customExpanded && (
            <>
              <div className="clbb-row">
                <div className="clbb-col">
                  <div className="clbb-label">Desde</div>
                  <DateField
                    value={dateStart}
                    max={todayInChile()}
                    onChange={(d) => {
                      if (!d) return;
                      dispatch(setTimelinePlaying(false));
                      const end = (dateEnd && d > dateEnd) ? d : (dateEnd || d);
                      dispatch(setDateRange({ start: d, end }));
                    }} />
                </div>
                <div className="clbb-col">
                  <div className="clbb-label">Hasta</div>
                  <DateField
                    value={dateEnd}
                    min={dateStart}
                    max={todayInChile()}
                    onChange={(d) => {
                      if (!d) return;
                      dispatch(setTimelinePlaying(false));
                      const start = (dateStart && d < dateStart) ? d : (dateStart || d);
                      dispatch(setDateRange({ start, end: d }));
                    }} />
                </div>
              </div>
              <div className="clbb-row">
                <div className="clbb-col" style={{ flex: "1 1 100%" }}>
                  <div className="clbb-label" style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Horario</span>
                    <label className="clbb-checkbox" style={{ fontSize: 11, fontWeight: 400 }}>
                      <input type="checkbox"
                             checked={!timeStart && !timeEnd}
                             onChange={(e) => {
                               userPickedTime.current = true;
                               dispatch(setTimelinePlaying(false));
                               if (e.target.checked) {
                                 dispatch(setTimeRange({ start: null, end: null }));
                               } else {
                                 const hourNow = new Intl.DateTimeFormat("en-CA", {
                                   timeZone: "America/Santiago", hour: "2-digit", hour12: false,
                                 }).format(new Date());
                                 dispatch(setTimeRange({ start: `${hourNow}:00`, end: `${hourNow}:59` }));
                               }
                             }} />
                      <span>Día completo</span>
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <HourField
                      value={timeStart}
                      disabled={!timeStart && !timeEnd}
                      maxTime={dateStart === todayInChile() && !timeEnd ? nowHHMMInChile() : undefined}
                      onChange={(v) => {
                        if (!v) return;
                        userPickedTime.current = true;
                        dispatch(setTimelinePlaying(false));
                        const end = (timeEnd && v <= timeEnd) ? timeEnd : v;
                        dispatch(setTimeRange({ start: v, end }));
                      }} />
                    <HourField
                      value={timeEnd}
                      disabled={!timeStart && !timeEnd}
                      minTime={timeStart || undefined}
                      maxTime={dateEnd === todayInChile() ? nowHHMMInChile() : undefined}
                      onChange={(v) => {
                        if (!v) return;
                        userPickedTime.current = true;
                        dispatch(setTimelinePlaying(false));
                        const start = (timeStart && v >= timeStart) ? timeStart : v;
                        dispatch(setTimeRange({ start, end: v }));
                      }} />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Comparar con otro período (B) */}
          <div className="clbb-compare-row">
            <label className="clbb-checkbox">
              <input type="checkbox" checked={compareEnabled}
                     onChange={(e) => dispatch(setCompareEnabled(e.target.checked))} />
              <span>Comparar con otro período</span>
            </label>
            {compareEnabled && (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  <DateField
                    accent="blue"
                    value={compareDateStart}
                    max={todayInChile()}
                    placeholder="Desde"
                    onChange={(d) => {
                      if (!d) return;
                      const end = (compareDateEnd && d > compareDateEnd) ? d : (compareDateEnd || d);
                      dispatch(setCompareDateRange({ start: d, end }));
                    }} />
                  <DateField
                    accent="blue"
                    value={compareDateEnd}
                    min={compareDateStart}
                    max={todayInChile()}
                    placeholder="Hasta"
                    onChange={(d) => {
                      if (!d) return;
                      const start = (compareDateStart && d < compareDateStart) ? d : (compareDateStart || d);
                      dispatch(setCompareDateRange({ start, end: d }));
                    }} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <HourField
                    accent="blue"
                    value={compareTimeStart}
                    disabled={!compareTimeStart && !compareTimeEnd}
                    placeholder="Desde"
                    onChange={(v) => {
                      if (!v) return;
                      const end = (compareTimeEnd && v <= compareTimeEnd) ? compareTimeEnd : v;
                      dispatch(setCompareTimeRange({ start: v, end }));
                    }} />
                  <HourField
                    accent="blue"
                    value={compareTimeEnd}
                    disabled={!compareTimeStart && !compareTimeEnd}
                    minTime={compareTimeStart || undefined}
                    placeholder="Hasta"
                    onChange={(v) => {
                      if (!v) return;
                      const start = (compareTimeStart && v >= compareTimeStart) ? compareTimeStart : v;
                      dispatch(setCompareTimeRange({ start, end: v }));
                    }} />
                </div>
                <label className="clbb-checkbox" style={{ fontSize: 11 }}>
                  <input type="checkbox"
                         checked={!compareTimeStart && !compareTimeEnd}
                         onChange={(e) => {
                           if (e.target.checked) {
                             dispatch(setCompareTimeRange({ start: null, end: null }));
                           } else {
                             dispatch(setCompareTimeRange({
                               start: timeStart || "00:00",
                               end: timeEnd || "23:59",
                             }));
                           }
                         }} />
                  <span>Día completo</span>
                </label>
              </div>
            )}
            {compareEnabled && compareDateStart && (
              <div className="clbb-mode-picker">
                <div className="clbb-label" style={{ marginTop: 10 }}>Modo de comparación</div>
                <div className="clbb-mode-segmented">
                  {COMPARE_MODES.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      className={`clbb-mode-btn ${compareMode === m.id ? "active" : ""}`}
                      onClick={() => dispatch(setCompareMode(m.id))}
                      title={m.help}
                    >
                      <span className="material-icons">{m.icon}</span>
                      <span className="clbb-mode-btn-label">{m.label}</span>
                    </button>
                  ))}
                </div>
                <div className="clbb-mode-help">
                  {COMPARE_MODES.find((m) => m.id === compareMode)?.help}
                </div>
              </div>
            )}
          </div>

          {/* Hour mini-bars */}
          {hoursBars.length > 0 && (
            <div className="clbb-section">
              <div className="clbb-label-row">
                <span className="clbb-label">Actividad por hora</span>
                <button className={`clbb-play ${timelinePlaying ? "playing" : ""}`}
                        onClick={() => dispatch(setTimelinePlaying(!timelinePlaying))}
                        title={timelinePlaying ? "Pausar" : "Play"}>
                  <span className="material-icons">
                    {timelinePlaying ? "pause" : "play_arrow"}
                  </span>
                </button>
              </div>
              <div className="clbb-hour-bars">
                {hoursBars.map(({ hh, jams, alerts }, i) => {
                  const hJ = Math.max(2, Math.round((jams / maxJams) * 28));
                  const hA = Math.max(0, Math.round((alerts / maxAlerts) * 10));
                  const active = selectedHour === hh;
                  const allDay = selectedHour === null;
                  const colorJ = jams === 0 ? "#eee" : (active ? "#111" : allDay ? "#f5c518" : "#ddd");
                  const bJams = hoursBarsB ? hoursBarsB[i].jams : null;
                  const hJB = bJams != null ? Math.max(2, Math.round((bJams / maxJams) * 28)) : null;
                  return (
                    <div key={hh} className="clbb-hb"
                         onClick={() => jams > 0 && dispatch(setTimeRange({ start: `${hh}:00`, end: `${hh}:59` }))}
                         title={`${hh}:00 — A ${jams}${bJams != null ? ` · B ${bJams}` : ""} jams · ${alerts} alerts`}>
                      <div className="clbb-hb-pair">
                        <div className="clbb-hb-jams" style={{ height: hJ, background: colorJ }} />
                        {hJB != null && <div className="clbb-hb-jams-b" style={{ height: hJB }} />}
                      </div>
                      <div className="clbb-hb-alerts" style={{ height: hA, background: alerts > 0 ? "#ff7b02" : "transparent" }} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {error && <div className="clbb-error">{error}</div>}

          {/* Veredicto global de la comparación (nivel de congestión A vs B) */}
          {compareVerdict && (
            <div className={`clbb-verdict ${compareVerdict.dir}`}>
              <span className="material-icons">
                {compareVerdict.dir === "up" ? "trending_up"
                  : compareVerdict.dir === "down" ? "trending_down" : "trending_flat"}
              </span>
              {compareVerdict.dir === "flat"
                ? <span>A (<strong>{fmtSideDate(dateStart, dateEnd)}</strong>) y B (<strong>{fmtSideDate(compareDateStart, compareDateEnd)}</strong>) con congestión similar</span>
                : <span>A (<strong>{fmtSideDate(dateStart, dateEnd)}</strong>) tuvo {compareVerdict.pct > 0 ? "+" : ""}{compareVerdict.pct.toFixed(0)}% de congestión vs B (<strong>{fmtSideDate(compareDateStart, compareDateEnd)}</strong>)</span>}
            </div>
          )}

          {/* Indicadores grandes. En comparar se apilan a ancho completo. */}
          <div className={`clbb-kpi-cards${compareEnabled && kpisB ? " clbb-kpi-cards--ab" : ""}`}>
            <KpiCard
              icon="directions_car"
              label="Frecuencia"
              value={kpis.count}
              unit="atascos"
              compare={kpisB?.count}
              info="Número total de eventos de congestión en un período de tiempo específico, proporcionando información sobre la cantidad de congestión que afectan las principales arterias de la ciudad."
            />
            <KpiCard
              icon="schedule"
              label="Duración media"
              value={kpis.avg_duration_min || "—"}
              unit="min"
              compare={kpisB?.avg_duration_min}
              info="Mide la duración promedio de la congestión (en minutos), evaluando cuánto tiempo, en promedio, persisten las congestiones en cada área."
            />
            <KpiCard
              icon="trending_up"
              label="Longitud media"
              value={kpis.avg_length_m || "—"}
              unit="m"
              compare={kpisB?.avg_length_m}
              info="Promedia la longitud de cada congestión reportada (metros), indicando qué tan extensos son los segmentos de tráfico congestionado en el área."
            />
          </div>

          {/* Gauge nivel congestión (full-width). En modo comparar se oculta:
              el gauge con doble aguja A/B resultaba confuso. */}
          {!compareEnabled && (
            <div className="clbb-section clbb-card">
              <div className="clbb-label">NIVEL CONGESTIÓN PROMEDIO<InfoTip text="Mide la severidad promedio de las congestiones usando una escala numérica (1 a 5), considerando como ponderador su longitud media." /></div>
              <Gauge value={kpis.avg_level || 0} max={5} />
            </div>
          )}

          {/* Gravedad accidentes. En modo comparar con datos de alertas en B se
              muestra como gráfico de barras A vs B (más claro que los badges). */}
          <div className="clbb-section">
            <div className="clbb-label">GRAVEDAD ACCIDENTES REPORTADOS<InfoTip text="Asigna un puntaje de severidad a los accidentes reportados según su tipo y calcula un promedio de gravedad." /></div>
            {compareEnabled && kpisB && hasBGravity ? (
              <>
                <div className="clbb-ab-legend">
                  <span><i className="clbb-ab-dot clbb-ab-a" /> A</span>
                  <span><i className="clbb-ab-dot clbb-ab-b" /> B</span>
                </div>
                <div className="clbb-grav-chart">
                  <Bar
                    data={{
                      labels: ["Mayor", "Menor", "Incidentes"],
                      datasets: [
                        { label: "A", data: [kpis.gravity.major, kpis.gravity.minor, kpis.gravity.incidents], backgroundColor: "#f5c518", borderRadius: 3 },
                        { label: "B", data: [kpisB.gravity.major, kpisB.gravity.minor, kpisB.gravity.incidents], backgroundColor: "#06b6d4", borderRadius: 3 },
                      ],
                    }}
                    options={{
                      responsive: true, maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                        y: { beginAtZero: true, grid: { color: "#f5f5f5" }, ticks: { font: { size: 9 }, precision: 0 } },
                      },
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="clbb-gravity-cards">
                  <div className="clbb-grav-card" style={{ "--accent": "#d71307" }}>
                    <span className="clbb-grav-card-dot" />
                    <div className="clbb-grav-card-num">{kpis.gravity.major}</div>
                    <div className="clbb-grav-card-label">Mayor gravedad<InfoTip text="Pueden incluir colisiones múltiples o incidentes que bloquean varias vías, lo cual puede causar demoras significativas." /></div>
                  </div>
                  <div className="clbb-grav-card" style={{ "--accent": "#ff7b02" }}>
                    <span className="clbb-grav-card-dot" />
                    <div className="clbb-grav-card-num">{kpis.gravity.minor}</div>
                    <div className="clbb-grav-card-label">Menor gravedad<InfoTip text="Colisiones leves o incidentes menores que pueden afectar solo una parte del camino, como choques que resultan en daños menores sin heridos graves." /></div>
                  </div>
                  <div className="clbb-grav-card" style={{ "--accent": "#ffcc01" }}>
                    <span className="clbb-grav-card-dot" />
                    <div className="clbb-grav-card-num">{kpis.gravity.incidents}</div>
                    <div className="clbb-grav-card-label">Incidentes<InfoTip text="Cantidad de alertas que no están catalogadas como accidentes, pero que pueden afectar la movilidad. Estos incidentes pueden incluir condiciones climáticas adversas, peligros en la carretera, construcción u otras alertas." /></div>
                  </div>
                </div>
                {compareEnabled && kpisB && !hasBGravity && (
                  <div className="clbb-grav-hint">Active la capa de alertas (⚠) para comparar la gravedad entre A y B.</div>
                )}
              </>
            )}
          </div>

          {/* Top streets */}
          {kpis.topStreets.length > 0 && (
            <div className="clbb-section">
              <div className="clbb-label-row">
                <span className="clbb-label">Frecuencia en ejes principales<InfoTip text="Muestra la frecuencia de congestión en arterias críticas de la ciudad en un período de tiempo específico, según posicionamiento espacial en el visor." /></span>
                {selectedStreet && (
                  <button className="clbb-chip-clear" onClick={() => setSelectedStreet(null)}>
                    <span className="material-icons">close</span>
                    Limpiar
                  </button>
                )}
              </div>
              {selectedStreet && (
                <div className="clbb-chip">
                  <span className="clbb-chip-dot" />
                  {selectedStreet}
                </div>
              )}
              {kpisB && (
                <div className="clbb-ab-legend">
                  <span><i className="clbb-ab-dot clbb-ab-a" /> A</span>
                  <span><i className="clbb-ab-dot clbb-ab-b" /> B</span>
                </div>
              )}
              <div className="clbb-bar-chart">
                <Bar
                  data={{
                    ...topStreetsData,
                    datasets: topStreetsData.datasets.map((ds) =>
                      ds.label === "B"
                        ? ds
                        : {
                            ...ds,
                            backgroundColor: kpis.topStreets.map((s) =>
                              s.name === selectedStreet
                                ? (topStreetsData.datasets.length > 1 ? "#d4a814" : "#5082ff")
                                : "#f5c518",
                            ),
                          },
                    ),
                  }}
                  options={{
                    indexAxis: "y",
                    responsive: true, maintainAspectRatio: false,
                    onHover: (e, els) => {
                      e.native.target.style.cursor = els.length ? "pointer" : "default";
                    },
                    onClick: (e, els) => {
                      if (!els.length) return;
                      const idx = els[0].index;
                      const street = kpis.topStreets[idx]?.name;
                      if (!street) return;
                      // Toggle: si ya está seleccionada, des-seleccionar.
                      if (street === selectedStreet) {
                        setSelectedStreet(null);
                        return;
                      }
                      setSelectedStreet(street);
                      // fitBounds al bbox de las features de la calle.
                      const calle = features.filter((f) => f.properties?.street === street);
                      if (!calle.length) return;
                      let mnX = Infinity, mnY = Infinity, mxX = -Infinity, mxY = -Infinity;
                      for (const f of calle) {
                        const g = f.geometry;
                        if (!g) continue;
                        const coords = g.type === "Point" ? [g.coordinates] : (g.coordinates || []);
                        for (const [x, y] of coords) {
                          if (x < mnX) mnX = x; if (x > mxX) mxX = x;
                          if (y < mnY) mnY = y; if (y > mxY) mxY = y;
                        }
                      }
                      if (!Number.isFinite(mnX)) return;
                      try {
                        const vp = new WebMercatorViewport({
                          width: window.innerWidth, height: window.innerHeight, ...viewState,
                        });
                        const fit = vp.fitBounds([[mnX, mnY], [mxX, mxY]], { padding: 100 });
                        const next = {
                          longitude: fit.longitude, latitude: fit.latitude,
                          zoom: Math.min(Math.max(fit.zoom, 13), 16),
                          pitch: 0, bearing: 0,
                        };
                        setViewState(next);
                        setStableViewState(next);
                      } catch {}
                    },
                    plugins: { legend: { display: false } },
                    scales: {
                      x: { grid: { color: "#f5f5f5" }, ticks: { font: { size: 9 } } },
                      y: { grid: { display: false }, ticks: { font: { size: 10 } } },
                    },
                  }}
                />
              </div>
            </div>
          )}

        </div>
        </div>
      </div>

      {/* Drawer toggle (solo mobile cuando shell collapsed) */}
      {collapsed && (
        <div className="clbb-bar" onClick={() => setCollapsed(false)}>
          <span className="material-icons">menu_open</span>
        </div>
      )}

      {/* Floating top-right: contadores accidentes/incidentes */}
      <div className="clbb-popup-openers">
        <div className="clbb-popup-card">
          <span className="material-icons" style={{ color: "#d71307" }}>directions_car</span>
          <span>N° accidentes reportados</span>
          <strong style={{ color: "#d71307" }}>{kpis.accidents_count}</strong>
        </div>
        <div className="clbb-popup-card">
          <span className="material-icons" style={{ color: "#f5c518" }}>warning</span>
          <span>N° incidentes reportados</span>
          <strong style={{ color: "#f5c518" }}>{kpis.incidents_count}</strong>
        </div>
      </div>

      {/* Mapa — el layout cambia según compareMode (overlay / swipe /
          split / diff / blink). Para modos multi-canvas se montan dos
          DeckGL+MapLibre con viewState compartido. */}
      <div className={`clbb-map-stage clbb-mode-${compareEnabled ? compareMode : "single"}`}>
        {compareEnabled && compareMode === "split" && compareGeojson ? (
          <div className="clbb-split-wrap">
            <div className="clbb-split-half">
              <DeckGL
                pickingRadius={8}
                ref={deckRef}
                layers={mainLayers}
                viewState={viewState}
                onViewStateChange={onSharedViewStateChange}
                views={new MapViewComponent({ repeat: true })}
                controller={{
                  scrollZoom: { smooth: false, speed: 0.01 },
                  dragPan: true, dragRotate: is3D, inertia: false,
                }}
              >
                <MapLibreMap mapStyle={BASEMAP_STYLE} />
                {hoverInfo?.object && !selectedFeature && hoverInfo._side === "A" && (
                  <CustomTooltip
                    x={hoverInfo.x} y={hoverInfo.y}
                    feature={hoverInfo.object}
                    dateA={selectedDate}
                    dateB={compareDate}
                    compareFeatures={compareSameStreet(hoverInfo.object)}
                  />
                )}
              </DeckGL>
              <div className="clbb-side-label clbb-side-a">A · {fmtSideDate(dateStart, dateEnd)} · {fmtSideTime(timeStart, timeEnd)}</div>
            </div>
            <div className="clbb-split-half clbb-half-b">
              <DeckGL
                pickingRadius={8}
                layers={bLayers}
                viewState={viewState}
                onViewStateChange={onSharedViewStateChange}
                views={new MapViewComponent({ repeat: true })}
                controller={{
                  scrollZoom: { smooth: false, speed: 0.01 },
                  dragPan: true, dragRotate: is3D, inertia: false,
                }}
              >
                <MapLibreMap mapStyle={BASEMAP_STYLE_B} />
                {hoverInfo?.object && !selectedFeature && hoverInfo._side === "B" && (
                  <CustomTooltip
                    x={hoverInfo.x} y={hoverInfo.y}
                    feature={hoverInfo.object}
                    dateA={compareDate}
                    dateB={selectedDate}
                    compareFeatures={sameStreetA(hoverInfo.object)}
                    side="B"
                  />
                )}
              </DeckGL>
              <div className="clbb-side-label clbb-side-b">B · {fmtSideDate(compareDateStart, compareDateEnd)} · {fmtSideTime(compareTimeStart, compareTimeEnd)}</div>
            </div>
          </div>
        ) : compareEnabled && compareMode === "swipe" && compareGeojson ? (
          <div className="clbb-swipe-wrap" ref={swipeRef}>
            {/* Capa base = B (toda la superficie) */}
            <div className="clbb-swipe-layer clbb-half-b">
              <DeckGL
                pickingRadius={8}
                layers={bLayers}
                viewState={viewState}
                onViewStateChange={onSharedViewStateChange}
                views={new MapViewComponent({ repeat: true })}
                controller={{
                  scrollZoom: { smooth: false, speed: 0.01 },
                  dragPan: true, dragRotate: is3D, inertia: false,
                }}
              >
                <MapLibreMap mapStyle={BASEMAP_STYLE_B} />
                {hoverInfo?.object && !selectedFeature && hoverInfo._side === "B" && (
                  <CustomTooltip
                    x={hoverInfo.x} y={hoverInfo.y}
                    feature={hoverInfo.object}
                    dateA={compareDate}
                    dateB={selectedDate}
                    compareFeatures={sameStreetA(hoverInfo.object)}
                    side="B"
                  />
                )}
              </DeckGL>
            </div>
            {/* Capa superior = A, clipeada a la izquierda del divisor */}
            <div
              className="clbb-swipe-layer clbb-swipe-top"
              style={{ width: `${swipePos * 100}%` }}
            >
              <div className="clbb-swipe-top-inner">
              <DeckGL
                pickingRadius={8}
                ref={deckRef}
                layers={mainLayers}
                viewState={viewState}
                onViewStateChange={onSharedViewStateChange}
                views={new MapViewComponent({ repeat: true })}
                controller={{
                  scrollZoom: { smooth: false, speed: 0.01 },
                  dragPan: true, dragRotate: is3D, inertia: false,
                }}
              >
                <MapLibreMap mapStyle={BASEMAP_STYLE} />
                {hoverInfo?.object && !selectedFeature && hoverInfo._side === "A" && (
                  <CustomTooltip
                    x={hoverInfo.x} y={hoverInfo.y}
                    feature={hoverInfo.object}
                    dateA={selectedDate}
                    dateB={compareDate}
                    compareFeatures={compareSameStreet(hoverInfo.object)}
                  />
                )}
              </DeckGL>
              </div>
            </div>
            {/* Handle del divisor */}
            <div
              className="clbb-swipe-handle"
              style={{ left: `${swipePos * 100}%` }}
              onMouseDown={(e) => { swipeDragging.current = true; e.preventDefault(); }}
              onTouchStart={() => { swipeDragging.current = true; }}
            >
              <div className="clbb-swipe-handle-knob">
                <span className="material-icons">drag_indicator</span>
              </div>
            </div>
            {/* Etiquetas pegadas al divisor: A a su izquierda, B a su derecha. */}
            <div className="clbb-side-label clbb-side-a"
                 style={{ right: `calc(${(1 - swipePos) * 100}% + 8px)` }}>A · {fmtSideDate(dateStart, dateEnd)} · {fmtSideTime(timeStart, timeEnd)}</div>
            <div className="clbb-side-label clbb-side-b"
                 style={{ left: `calc(${swipePos * 100}% + 8px)` }}>B · {fmtSideDate(compareDateStart, compareDateEnd)} · {fmtSideTime(compareTimeStart, compareTimeEnd)}</div>
          </div>
        ) : (
          <div style={{ height: "100%", width: "100%" }}>
            <DeckGL
                pickingRadius={8}
              ref={deckRef}
              layers={mainLayers}
              viewState={viewState}
              onViewStateChange={onSharedViewStateChange}
              views={new MapViewComponent({ repeat: true })}
              controller={{
                scrollZoom: { smooth: false, speed: 0.01 },
                dragPan: true, dragRotate: is3D, inertia: false,
              }}
            >
              <MapLibreMap mapStyle={BASEMAP_STYLE} reuseMaps />
              {hoverInfo?.object && !selectedFeature && hoverInfo._side === "A" && (
                <CustomTooltip
                  x={hoverInfo.x}
                  y={hoverInfo.y}
                  feature={hoverInfo.object}
                  dateA={selectedDate}
                  dateB={compareEnabled ? compareDate : null}
                  compareFeatures={compareSameStreet(hoverInfo.object)}
                />
              )}
            </DeckGL>
            {/* Badge del lado activo en modo blink */}
            {compareEnabled && compareMode === "blink" && compareGeojson && (
              <div className={`clbb-blink-badge clbb-blink-${blinkSide.toLowerCase()}`}>
                {blinkSide === "A"
                  ? `A · ${selectedDate}`
                  : `B · ${compareDate}`}
              </div>
            )}
            {/* Leyenda del modo diff */}
            {compareEnabled && compareMode === "diff" && compareGeojson && (
              <div className="clbb-diff-legend">
                <div className="clbb-diff-legend-title">Cambio A → B (por calle)</div>
                <div className="clbb-diff-legend-row"><i style={{ background: "rgb(215,19,7)" }} />Empeoró</div>
                <div className="clbb-diff-legend-row"><i style={{ background: "rgb(40,180,70)" }} />Mejoró</div>
                <div className="clbb-diff-legend-row"><i style={{ background: "rgb(150,150,150)" }} />Sin cambio</div>
                <div className="clbb-diff-legend-row"><i style={{ background: "rgb(255,123,2)" }} />Sólo en A</div>
                <div className="clbb-diff-legend-row"><i style={{ background: "rgb(80,130,255)" }} />Sólo en B</div>
              </div>
            )}
          </div>
        )}
      </div>

      <SidebarDetail feature={selectedFeature}
                     onClose={() => dispatch(setSelectedFeature(null))} />

      {/* Control flotante de geolocalización sobre el mapa. */}
      <button
        type="button"
        className="clbb-geo-fab"
        onClick={geolocateMe}
        title="Centrar el mapa en mi ubicación"
        aria-label="Centrar en mi ubicación"
      >
        <span className="material-icons">my_location</span>
      </button>
    </>
  );
}


/** Computa los KPIs del panel a partir de features visibles (jams + alerts
 * ya filtradas por viewport/comuna). Pura y reutilizable para el período A y
 * el B (comparativa). */
function computeKpis(visJams, visAlerts, totalJams, totalAlerts, catalog) {
  const sum = (arr, key) => arr.reduce((a, f) => a + (f.properties?.[key] || 0), 0);
  const avg = (arr, key) => arr.length ? sum(arr, key) / arr.length : 0;

  // Dedup de atascos por uuid: el mismo atasco aparece en cada snapshot de
  // 2 min mientras está activo. 1 uuid = 1 atasco.
  const uniqJams = [];
  const seenJams = new Set();
  for (const f of visJams) {
    const p = f.properties || {};
    const key = p.uuid
      || `${p.street}|${(f.geometry?.coordinates?.[0] || []).join(",")}`;
    if (seenJams.has(key)) continue;
    seenJams.add(key);
    uniqJams.push(f);
  }

  const durationMin = uniqJams.length
    ? uniqJams.reduce((a, f) => {
        const p = f.properties || {};
        if (Number.isFinite(p.delay)) return a + p.delay / 60;
        if (Number.isFinite(p.length) && Number.isFinite(p.speedKMH) && p.speedKMH > 0) {
          return a + (p.length / (p.speedKMH * 1000 / 3600)) / 60;
        }
        return a;
      }, 0) / uniqJams.length
    : 0;

  const streetCounts = {};
  for (const f of uniqJams) {
    const s = f.properties?.street;
    if (s) streetCounts[s] = (streetCounts[s] || 0) + 1;
  }
  const topStreets = Object.entries(streetCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, count]) => ({ name, count }));

  // Dedup de alertas por uuid (idem atascos).
  const uniqAlerts = [];
  const seenAlerts = new Set();
  for (const f of visAlerts) {
    const p = f.properties || {};
    const key = p.uuid
      || `${p.type}|${p.subtype}|${(f.geometry?.coordinates || []).join(",")}`;
    if (seenAlerts.has(key)) continue;
    seenAlerts.add(key);
    uniqAlerts.push(f);
  }

  let major = 0, minor = 0, incidents = 0;
  for (const f of uniqAlerts) {
    const sub = f.properties?.subtype;
    const type = f.properties?.type;
    if (sub === "ACCIDENT_MAJOR") major++;
    else if (sub === "ACCIDENT_MINOR") minor++;
    else if (type === "ACCIDENT") incidents++;
    else incidents++;
  }

  return {
    count: uniqJams.length,
    count_total: totalJams,
    alerts_count: uniqAlerts.length,
    alerts_count_total: totalAlerts,
    avg_level: avg(uniqJams, "level"),
    avg_length_m: Math.round(avg(uniqJams, "length")),
    avg_duration_min: Math.round(durationMin * 100) / 100,
    snapshots: catalog?.snapshots_count ?? null,
    peak_hour: catalog?.peak_hour ?? null,
    topStreets,
    gravity: { major, minor, incidents },
    accidents_count: uniqAlerts.filter(f => f.properties?.type === "ACCIDENT").length,
    incidents_count: uniqAlerts.filter(f => f.properties?.type !== "ACCIDENT").length,
  };
}

// Tooltip informativo (hover/focus) con el texto descriptivo del indicador.
// Se renderiza vía portal a <body> con posición fija para que no lo recorte el
// panel con scroll. Incluye un slot punteado reservado para un ícono futuro.
const InfoTip = ({ text }) => {
  const [pos, setPos] = useState(null);
  const onEnter = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: r.right + 8, y: r.top + r.height / 2 });
  };
  const hide = () => setPos(null);
  return (
    <span
      className="clbb-infotip"
      tabIndex={0}
      onMouseEnter={onEnter}
      onMouseLeave={hide}
      onFocus={onEnter}
      onBlur={hide}
    >
      <span className="material-icons clbb-infotip-icon">info_outline</span>
      {pos && createPortal(
        <span className="clbb-infotip-pop" style={{ left: pos.x, top: pos.y }} role="tooltip">
          <span className="clbb-infotip-ico-slot" aria-hidden="true" />
          <span className="clbb-infotip-text">{text}</span>
        </span>,
        document.body,
      )}
    </span>
  );
};

const KpiCard = ({ icon, label, value, unit, compare, info }) => {
  const a = Number(value);
  const b = Number(compare);
  const showAB = compare !== null && compare !== undefined
    && Number.isFinite(a) && Number.isFinite(b);

  // Modo comparar: card a ancho completo con barras A vs B (sin número grande
  // ni badge), igual criterio que el gráfico de gravedad — más fácil de leer.
  if (showAB) {
    const mx = Math.max(Math.abs(a), Math.abs(b), 1);
    const wA = `${(Math.abs(a) / mx) * 100}%`;
    const wB = `${(Math.abs(b) / mx) * 100}%`;
    return (
      <div className="clbb-kpi clbb-kpi--ab">
        <div className="clbb-kpi-label">{label}{info && <InfoTip text={info} />}</div>
        <div className="clbb-kpi-ab">
          <div className="clbb-kpi-ab-row">
            <span className="clbb-kpi-ab-tag clbb-tag-a">A</span>
            <span className="clbb-kpi-ab-track"><span className="clbb-kpi-ab-fill a" style={{ width: wA }} /></span>
            <span className="clbb-kpi-ab-num">{value}{unit ? ` ${unit}` : ""}</span>
          </div>
          <div className="clbb-kpi-ab-row">
            <span className="clbb-kpi-ab-tag clbb-tag-b">B</span>
            <span className="clbb-kpi-ab-track"><span className="clbb-kpi-ab-fill b" style={{ width: wB }} /></span>
            <span className="clbb-kpi-ab-num">{compare}{unit ? ` ${unit}` : ""}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="clbb-kpi">
      <div className="clbb-kpi-label">{label}{info && <InfoTip text={info} />}</div>
      <div className="clbb-kpi-value">{value}</div>
      <div className="clbb-kpi-unit">{unit}</div>
      <div className="clbb-kpi-icon"><span className="material-icons">{icon}</span></div>
    </div>
  );
};

/** Gauge semicircular SVG: arco background gris + arco coloreado por
 * dasharray según value/max, aguja rotada y valor central. */
const Gauge = ({ value, max = 5, compareValue = null }) => {
  const v = Math.max(0, Math.min(max, value));
  const pct = v / max;

  // Dimensiones SVG: half-circle de radio 80 centrado en (100, 100).
  const cx = 100, cy = 100, r = 80;
  const ARC_LEN = Math.PI * r;  // longitud de medio arco
  const visible = pct * ARC_LEN;
  const needleAngle = -90 + pct * 180;  // -90 (left) → 90 (right)

  // Aguja del período B (comparativa), si hay valor.
  const hasCompare = compareValue !== null && compareValue !== undefined
    && Number.isFinite(Number(compareValue));
  const vb = hasCompare ? Math.max(0, Math.min(max, Number(compareValue))) : 0;
  const needleAngleB = -90 + (vb / max) * 180;

  return (
    <div className="clbb-gauge-svg">
      <svg viewBox="0 12 200 116" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="gauge-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="#28a446" />
            <stop offset="25%"  stopColor="#ffcc01" />
            <stop offset="50%"  stopColor="#ffad03" />
            <stop offset="75%"  stopColor="#ff7b02" />
            <stop offset="100%" stopColor="#d71307" />
          </linearGradient>
        </defs>
        {/* Track (gris) */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
              stroke="#f0f0f0" strokeWidth="14" fill="none" strokeLinecap="round" />
        {/* Arco coloreado, hasta pct */}
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
              stroke="url(#gauge-grad)" strokeWidth="14" fill="none" strokeLinecap="round"
              strokeDasharray={`${visible} ${ARC_LEN}`}
              style={{ transition: "stroke-dasharray 0.6s ease" }} />
        {/* Aguja B (comparativa) — azul punteada, debajo de la de A.
            Rotación por transform CSS + transform-origin en el pivote:
            interpola como rotación pura (sin wobble/traslación). */}
        {hasCompare && (
          <g style={{
                transform: `rotate(${needleAngleB}deg)`,
                transformOrigin: `${cx}px ${cy}px`,
                transition: "transform 0.6s ease",
              }}>
            <line x1={cx} y1={cy} x2={cx} y2={cy - r + 16}
                  stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round"
                  strokeDasharray="3 4" />
          </g>
        )}
        {/* Aguja A */}
        <g style={{
              transform: `rotate(${needleAngle}deg)`,
              transformOrigin: `${cx}px ${cy}px`,
              transition: "transform 0.6s ease",
            }}>
          <line x1={cx} y1={cy} x2={cx} y2={cy - r + 8}
                stroke="#111" strokeWidth="3" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r="7" fill="#fff" stroke="#111" strokeWidth="2.5" />
        </g>
        {/* Marcas de escala 1..5 */}
        {[0, 1, 2, 3, 4, 5].map((tick) => {
          const ang = -Math.PI + (tick / 5) * Math.PI;
          const x1 = cx + Math.cos(ang) * (r - 14);
          const y1 = cy + Math.sin(ang) * (r - 14);
          const x2 = cx + Math.cos(ang) * (r - 22);
          const y2 = cy + Math.sin(ang) * (r - 22);
          const lx = cx + Math.cos(ang) * (r - 32);
          const ly = cy + Math.sin(ang) * (r - 32) + 3;
          return (
            <g key={tick}>
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" />
              <text x={lx} y={ly} textAnchor="middle"
                    fontSize="9" fontWeight="600" fill="#888">{tick}</text>
            </g>
          );
        })}
        {/* Valor central */}
        <text x={cx} y={cy + 20} textAnchor="middle"
              fontSize="24" fontWeight="800" fill="#111">{v.toFixed(1)}</text>
      </svg>
    </div>
  );
};

